import React, { useState, useEffect, useMemo } from 'react';
import { capitalReturnedWords, capitalAccessPeriodFor } from '../../components/strategy/annuityWords.ts';
import { SameAsBestEstateNote } from '../../components/strategy/SameAsBestEstateNote.tsx';
import { pickCashflowCoveredLeader, cashflowCoveredNote, CASHFLOW_COVERED_LABEL, CASHFLOW_COVERED_CHIP, BEST_ESTATE_CHIP, GOAL_CHIP_LABELS, friendlyGoalLabel, coveredIsBestEstate, SAME_PLAN_NOTE } from '../../strategy/cashflowCoveredGoal.ts';
import { scrollAppToTop } from '../../utils/scrollAppToTop.ts';
import { ChevronLeft, ArrowRight, Banknote, DollarSign, Home, Building, Wallet, Gift, LogOut, ShieldCheck, TrendingUp as TrendingUpIcon, CheckCircle2, ChevronDown, Scale, Heart, AlertTriangle, Users, Sparkles, FileSignature, Clock, Repeat, type LucideIcon } from 'lucide-react';
import { Card, GradientText, CurrencyInput, InfoIcon, FeeExplanationAccordion, AllocationInput, Button, IconTile, Badge } from '../../components/ui/index.tsx';
import { buildRetirementVillageSuggestions, generateRetirementVillageCandidates } from '../../strategy/retirementVillageRecommendedStrategies.ts';
import { mergeGoalLeaderStrategies, buildRecommendedOptimalStrategies } from '../../strategy/supportAtHomeRecommendedStrategies.ts';
import * as calculationApi from '../../services/calculationApi.ts';
import {
    getVillageCostSummary,
    computeInvestmentPropertyNetForAmount,
    computeRvEntryFeeFundingTotals,
    buildSimulatedFactFindForVillageStrategy,
    buildVillageMeansAssessmentOptions,
} from '../../../../shared/src/calculation/retirementVillageCalculations.ts';
import RVEntryFeeFunding, { resolveRvEntryFeeFunding, isAdviserAllocatedEntryFee } from './RVEntryFeeFunding.tsx';
import FinancialImpactPanel from '../agedCare/FinancialImpactPanel.tsx';
import PlanSummaryPanel from '../agedCare/PlanSummaryPanel.tsx';
import PlanExplanationModal from '../agedCare/PlanExplanationModal.tsx';
import { planRunwayCopy } from '../../components/insights/fundingSummary.tsx';
import { FundingRunway } from '../../components/narrative/index.tsx';
import RVOtherStrategiesSection from './RVOtherStrategiesSection.tsx';
import type { AnnuityDetails } from '../supportAtHome/SupportAtHomeAnnuityConfigurator.tsx';
import { buildAnnuityImpact, summariseAnnuityImpact } from '../../../../shared/src/calculation/annuityStrategies.ts';
import { INVESTMENT_PROFILES, newMoneyProfileOf, hasAnsweredNewMoneyProfile } from '../../../../shared/src/calculation/investmentProfiles.ts';
import type { InvestmentProfileId } from '../../../../shared/src/calculation/investmentProfiles.ts';
import NewMoneyProfileGate from '../../components/NewMoneyProfileGate.tsx';
import TransferCapGate from '../../components/TransferCapGate.tsx';
import { tbcQuestionNeededFor } from '../../../../shared/src/calculation/transferBalanceCap.ts';
import { buildNetWorthSchedule } from '../../../../shared/src/calculation/netWorthSchedule.ts';
import { formatCurrency, calculateAge } from '../../utils/index.ts';
import StampDutyFields from '../../components/ui/StampDutyFields.tsx';
import RvAnalysisSetupModal from './RvAnalysisSetupModal.tsx';
import { PlanPiecesCard } from '../../components/narrative/PlanPieces.tsx';
import { rvPlanPieces } from './rvPlanPieces.ts';
import { rvEngineSetupFromGoals, type RvEngineSetup } from '../../strategy/retirementVillageRecommendedStrategies.ts';
import { EXTRA_ALLOWABLE_AMOUNT, FUNERAL_BOND_LIMIT, DEFAULT_INFLATION_ASSUMPTION_PCT, RETIREMENT_VILLAGE_STATES, getRetirementVillageStateRules } from '../../constants/index.ts';
import RvPlanRecap from './RvPlanRecap.tsx';
import { RentedFormerHomeCgtNote, InvestmentPropertyEntryFeeNote } from './RvFieldNotes.tsx';
import DownsizerRulesAndInsights from '../../components/strategy/DownsizerRulesAndInsights.tsx';
import { EngineAnnuityTerms, AnnuityQuoteControl } from '../../components/strategy/AnnuityTerms.tsx';
import { NewMoneyProfileField } from '../../components/strategy/NewMoneyProfileField.tsx';
import RvStrategyCard, { annuityOfStrategy, annuityHorizonWarningOf } from './RvStrategyCard.tsx';
import StrategyRisksPanel from '../../components/strategy/StrategyRisksPanel.tsx';
import { buildStrategyRisks, riskKindsFromCandidateId } from '../../strategy/strategyRisks.ts';
import { strategyExplainers } from '../../strategy/strategyExplainers.ts';
import { alternativeAssetNote } from '../../strategy/alternativeAssetNote.ts';
import { buildRetirementVillageSoaSnapshot } from './rvSoa.ts';
import { openModuleSoa } from '../advice/moduleSoa.ts';
import RvStayPutHero from './RvStayPutHero.tsx';
import RvVillageCostsTile, { exitAfter } from './RvVillageCostsTile.tsx';
import { DEFAULT_HOME_GROWTH_PCT } from '../../../../shared/src/calculation/assetRateOverrides.ts';
import { RvInBrief, type RvInBriefAnnuity } from './rvInBrief.tsx';
import AnalyserSteps, { type AnalyserStep } from '../../components/strategy/AnalyserSteps.tsx';
import { ModeChooser } from '../../components/ui/index.tsx';
import { getRecommendedEmergencyFund } from '../../utils/emergencyFund.ts';
import SuggestionEngineAnalysisLoader from '../../components/ui/SuggestionEngineAnalysisLoader.tsx';
import RVInvestmentPropertyAllocator from './RVInvestmentPropertyAllocator.tsx';
import ProjectionsModal from '../agedCare/ProjectionsModal.tsx';
import { readPeriodMode, type ProjectionsModalPeriodMode } from '../agedCare/projectionsModalPreferences.ts';
import DebtPayoffStrategy from '../agedCare/DebtPayoffStrategy.tsx';
import type { DebtPayoff } from '../../../../shared/src/calculation/support/debtPayoff.ts';
import type { FyBlendView } from './RVProjectionsModal.tsx';
import type { RvFundFrom } from './rvTypes.ts';
import type { EvaluatedStrategy, RecommendedStrategyEntry } from '../../strategy/supportAtHomeRecommendedStrategies.ts';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import type { FactFindAsset, FactFind } from '../../../../shared/src/calculation/factFindTypes.ts';
import type { FinancialPosition, BreakdownRow } from '../agedCare/FinancialImpactPanel.tsx';
import { notify } from '../../components/ui/notices.tsx';

type DraftAsset = FactFindAsset;

/** One year of retirement-village projection output (calculationApi.runRetirementVillageProjections /
 *  evaluateRetirementVillageCandidates) — a free-form calc-engine result blob (see calculationApi.ts's
 *  own doc comment), typed here only for the fields this page actually reads. */
export interface RvProjectionRow {
    year?: number;
    netWorth?: number;
    netCashflow?: number;
    exitProceeds?: number;
    financials?: FinancialPosition;
    factFind?: DraftFactFind;
    assets?: Record<string, DraftAsset[] | undefined>;
    drawdownBreakdown?: BreakdownRow[];
    fyFullYearView?: FyBlendView;
    [key: string]: unknown;
}

/** A goal-leader strategy as merged/ranked by mergeGoalLeaderStrategies for the RV suggestion
 *  engine — EvaluatedStrategy plus the RV-specific fields this page reads off it. */
export interface RvEvaluatedStrategy extends EvaluatedStrategy {
    id?: string;
    projections?: RvProjectionRow[];
    inputs?: { fundFrom?: string; rentHome?: boolean; [key: string]: unknown };
    annuityIncome?: number;
    fundingBreakdown?: { label: string; amount: number }[];
    description?: string;
}

/** rvInsightFinancials useMemo result (RetirementVillageAnalyser) — passed to RetirementVillageInsightPanel. */
export interface RvInsightFinancials {
    agePensionBefore: number;
    agePensionAfter: number;
    agePensionWhy: string;
    cashflowBefore: number;
    cashflowAfter: number;
    villageOngoingAnnual: number;
    legacyEstimate: number;
    noVillageScenario: boolean;
    cashflowDeficitNarrative: string | null;
    estateTimingNote: string;
    rentAssistance: number;
}

/** investmentPropertySaleSummary useMemo result. */
export interface InvestmentPropertySaleSummary {
    netProceeds: number;
    estimatedCGT: number;
}

/** getVillageCostSummary(villageInputs) result. */
export interface VillageCostSummary {
    entryFee: number;
    annualOngoing: number;
    exitDmf: number;
    exitNetToResident: number;
    [key: string]: unknown;
}

/** Village cost/contract inputs (villageInputs state) — see getVillageCostSummary in
 *  shared/src/calculation/retirementVillageCalculations.ts for the shape these feed into.
 *  A `type` (not `interface`) so it structurally satisfies calculationApi.ts's
 *  `Record<string, unknown>`-typed ephemeral strategy-bag parameters without a cast at every call. */
export type VillageInputs = {
    entryFee: number;
    ongoingWeeklyOrMonthly: number;
    ongoingFrequency: 'weekly' | 'monthly';
    dmfPctPerYear: number;
    dmfCapYears: number;
    yearsResidency: number;
    growthRatePct: number;
    capitalGainShareToResident: number;
    reinstatementCap: number;
};

/** RVSellHomeAllocator's editable state (Sell/downsize the family home strategy). */
export type SellHomeDetails = {
    salePrice?: number;
    sellingCosts?: number;
    downsizerContribution?: number;
    partnerDownsizerContribution?: number;
    commenceIncomeStream?: boolean;
    giftingAmount?: number;
    funeralBondPurchase?: number;
    purchaseAnnuity?: number;
    rvAnnuityType?: 'lifetime' | 'fixedTerm' | 'guaranteed';
    rvAnnuityResidualPercent?: number;
    rvAnnuityTermYears?: number;
    rvAnnuityIndexation?: boolean;
    rvAnnuityReversionary?: boolean;
    rvAnnuityOwner?: 'client' | 'partner' | 'joint';
    rvAnnuityIncomeOverride?: number;
    purchaseInvestmentProperty?: number;
    investmentPropertyRent?: number;
    investmentPropertyExpenses?: number;
    /** The state the bought property is in and the duty on buying it — struck by StampDutyFields, overwritable. */
    investmentPropertyState?: string;
    investmentPropertyStampDuty?: number;
    purchaseShares?: number;
};

/** Rent-the-family-home strategy state. */
export type RentDetails = {
    rentAmount: number;
    rentExpenses: number;
    partnerRent: number;
};

/** RVInvestmentPropertyAllocator's editable state (Sell investment property strategy). */
export type RvInvestmentPropertyDetails = {
    selectedPropertyId?: number | string | null;
    salePrice?: number;
    sellingCosts?: number;
    costBase?: number;
    entryFeeAllocation?: number;
    downsizerContribution?: number;
    partnerDownsizerContribution?: number;
    commenceIncomeStream?: boolean;
    giftingAmount?: number;
    funeralBondPurchase?: number;
};

/** Gifting / funeral bond / annuity "other strategies" (RVOtherStrategiesSection). */
export type OtherStrategies = {
    gifting: boolean;
    giftingAmount: number;
    giftingSource: string;
    funeralBonds: boolean;
    funeralBondAmount: number;
    funeralBondSource: string;
    annuity: boolean;
};

export type RvOverallStrategy = {
    rentHome: boolean;
    downsizeHome: boolean;
    sellInvestmentProperty: boolean;
};

/** A saved strategy card (Save Strategy panel / Compare modal). Shared shape across the app's
 *  strategy features (aged care, SAH, RV) — inputs/outputs are strategy-specific free-form bags. */
export interface SavedStrategy {
    id: string;
    name: string;
    type: string;
    inputs: Record<string, unknown>;
    outputs: Record<string, unknown>;
    createdAt?: string;
    savedAt?: string;
}

const RV_EXPLAIN_SECTIONS = [
  { id: 'entry', icon: DollarSign, title: 'Entry payment', summary: 'A one-off amount when you move in—often called the ingoing contribution or purchase price. Many villages set this out in a disclosure statement.', details: <p className="text-gray-700 leading-relaxed">The entry payment is your upfront cost to secure your unit. It can be a large sum and may be close to market value for long-term lease or purchase arrangements. Additional one-off fees (e.g. garage, parking) may apply. Get the village&apos;s disclosure statement for exact figures.</p>, applies: true },
  { id: 'ongoing', icon: Banknote, title: 'Regular fees', summary: 'Weekly or monthly fees for services, maintenance and shared facilities. The disclosure statement usually shows how and when these can increase.', details: <p className="text-gray-700 leading-relaxed">Recurrent charges are split among residents from the village&apos;s annual budget. They typically cover management, insurance, maintenance, communal facilities (pool, gym, gardens) and 24-hour emergency. Optional services (meals, cleaning, laundry) are usually itemised and only charged if used. Contracts must state how and when fees can increase (e.g. CPI, notice periods).</p>, applies: true },
  { id: 'exit', icon: LogOut, title: 'When you leave', summary: "A departure fee (deferred management fee) is usually taken from the amount you get back. You may also get a share of any increase in the unit's value.", details: <p className="text-gray-700 leading-relaxed">The departure fee (DMF) is a major cost and is contract-specific—it must be in the disclosure statement. It is often a percentage per year of residency with a cap (e.g. 2% per year, capped at 20% after 10 years). The percentage can apply to your entry payment or to the amount the next resident pays. Other exit costs can include reinstatement (returning the unit to original condition) and refurbishment. Your village&apos;s disclosure statement will spell out the exact rules.</p>, applies: true },
  { id: 'contract-right', icon: Home, title: 'Right to live there (no ownership)', summary: "You pay an entry amount and regular fees but don't own the unit. When you leave, you get back an amount (often when the unit is re-sold) after the departure fee and other deductions.", details: <div className="text-gray-700 leading-relaxed space-y-2"><p>This is the most common arrangement — legally a <strong>loan or licence</strong>: your entry payment buys a contractual right to occupy under your state&apos;s Retirement Villages Act, while the operator keeps title. Practical consequences worth knowing:</p><ul className="list-disc pl-5 space-y-1"><li>You can&apos;t borrow against the unit or sell it yourself — your money comes back through the contract, not a property sale.</li><li>Your exit entitlement is usually paid when the unit is <strong>re-licensed to the next resident</strong>, so timing depends on how quickly the village re-sells. Some states guarantee payment after a set period — see the state protections further down this page.</li><li>Questions to ask before signing: who pays the regular fees between your leaving and the re-sale? Who sets the re-sale price and appoints the agent? What happens to your entitlement if the village is sold or the operator fails?</li></ul></div>, applies: true },
  { id: 'contract-own', icon: Building, title: 'You own the unit', summary: 'For example strata or community title. You own the unit (sometimes with rules on resale). When you leave, you sell it or the village may buy it back; the departure fee and contract terms still apply.', details: <div className="text-gray-700 leading-relaxed space-y-2"><p>With strata or community title you hold real title — which changes the mechanics, not the economics:</p><ul className="list-disc pl-5 space-y-1"><li>You may pay <strong>two sets of ongoing charges</strong>: strata levies for the building AND village fees for services — check what each covers.</li><li>Resale is often restricted to age-qualified buyers, which can slow the sale or soften the price compared with the open market; some contracts give the operator a buy-back right at a formula price.</li><li>Because you hold title, stamp duty usually applies on the way in — a real cost the loan/licence route avoids.</li><li>The departure fee still applies on exit in most ownership contracts — ownership does not escape the DMF.</li></ul></div>, applies: true },
  { id: 'pension', icon: Wallet, title: 'Age Pension impact', summary: `Services Australia uses your entry payment to decide homeowner status (threshold $${EXTRA_ALLOWABLE_AMOUNT.toLocaleString()}). Above = homeowner (entry not in assets); at or below = non-homeowner (entry counted, Rent Assistance possible).`, details: <div className="text-gray-700 leading-relaxed space-y-2"><p>Why the classification matters more than it sounds — a worked example:</p><ul className="list-disc pl-5 space-y-1"><li>Entry payment $600,000, threshold ${EXTRA_ALLOWABLE_AMOUNT.toLocaleString()}: you&apos;re a <strong>homeowner</strong> — the $600,000 vanishes from the assets test entirely, but your remaining assets are then measured against the <em>lower</em> homeowner thresholds.</li><li>Entry payment $200,000: you&apos;re a <strong>non-homeowner</strong> — the $200,000 IS counted, but you get the <em>higher</em> non-homeowner thresholds and may qualify for <strong>Rent Assistance</strong> on your regular fees, which can be worth thousands a year.</li><li>Money left over after the entry payment (say, from selling your home) becomes a financial asset — deemed for the income test and counted for the assets test. That&apos;s often the biggest pension effect of the whole move, and it&apos;s why the calculator models the entry payment and the leftover together rather than in isolation.</li></ul></div>, applies: true },
  { id: 'legacy', icon: Gift, title: 'What you leave to family', summary: 'The amount you get back when you leave (after the departure fee and other costs) is often paid when the unit is sold. That amount affects what you can leave to your family.', details: <div className="text-gray-700 leading-relaxed space-y-2"><p>Three things families are most often surprised by:</p><ul className="list-disc pl-5 space-y-1"><li><strong>The wait.</strong> The exit entitlement is a contract debt owed by the operator, not a property your estate can sell — it&apos;s typically paid only when the unit re-sells, which can take months (some state Acts set a backstop period — see the state section below). Until then it may be earning nothing while regular fees, in some states, continue.</li><li><strong>The departure fee grows with time.</strong> A 3%-per-year DMF capped at 10 years means an estate loses far more after a 10-year stay than after a 3-year one — the &quot;You get back (est.)&quot; figure in the calculator shows exactly this for your inputs.</li><li><strong>The gain share cuts both ways.</strong> If the contract gives you 50% of any capital gain, the other 50% never reaches your estate — and some contracts pass the resident a share of a capital <em>loss</em> too.</li></ul></div>, applies: true }
];

const RV_COST_TOOLTIPS = {
  entryFee: "The one-off ingoing contribution or purchase price when you move in. Often set out in the village's disclosure statement.",
  ongoingFees: "Weekly or monthly recurrent fees for maintenance, services and shared facilities. The disclosure statement usually shows how and when these can increase.",
  ongoingFrequency: "Whether your recurrent fees are quoted weekly or monthly.",
  dmfPctPerYear: "The departure fee — also called the deferred management fee (DMF) — is typically a percentage of your entry payment (or the resale value) per year you stay. Common contracts run 2–3% per year; take the exact figure from your disclosure statement, as it varies widely.",
  dmfCapYears: "How many years the departure fee keeps accruing before it stops. For example, 3% per year capped at 10 years means a maximum of 30%. Leave 0 if your contract has no cap (the fee then accrues for your whole stay).",
  yearsResidency: "Most people move in to stay, so we assume no sale: the fees keep going and the value of the contract stays in the estate. The costs panel shows what you would get back if you left after 3, 5 or 10 years. Choose a set number of years only if you expect to move out; the unit is then sold in that year and the fees stop.",
  growthRatePct: "Assumed annual growth in the unit's value, used to estimate the resale price. We start with a conservative 3% — adjust it for the village's location and history. Your contract sets how any gain is shared.",
  capitalGainShare: "Your share of any capital gain when you leave (e.g. 50% means you and the operator split the gain). Check the disclosure statement.",
  reinstatementCap: "Cost to return the unit to its original condition (less fair wear and tear) when you leave. Sometimes capped in the contract."
};

/**
 * The one help device on a form field (owner, 22 and 24 Sep 2026): a dotted "what's this?" that
 * opens a small card over the page — never an icon, and never an in-place expansion, which pushed
 * this two-column form apart. The shared InfoIcon; the aged care analyser uses the same one.
 */
const WhatsThis = ({ text }: { text: string }) => <InfoIcon text={text} />;

/** Numeric-input strings (mid-edit draft values, incl. '') coerced to a real number — the same
 *  defensive `parseFloat(String(x))` idiom used at calc-engine read sites (see
 *  shared/src/calculation/factFindTypes.ts). */
const n = (v: unknown): number => parseFloat(String(v ?? '')) || 0;

interface RVSellHomeAllocatorProps {
    factFind: DraftFactFind;
    /** Lets the client set the approach allocated shares are priced at, in the place they allocate. */
    setFactFind?: React.Dispatch<React.SetStateAction<DraftFactFind>>;
    details: SellHomeDetails;
    setDetails: React.Dispatch<React.SetStateAction<SellHomeDetails>>;
    entryFee: number;
}

function RVSellHomeAllocator({ factFind, setFactFind, details, setDetails, entryFee }: RVSellHomeAllocatorProps) {
    const handleDetailChange = (field: string, value: number | undefined) => {
        setDetails(prev => ({ ...prev, [field]: value ?? 0 }));
    };
    const netProceeds = Math.max(0, (details.salePrice || 0) - (details.sellingCosts || 0));
    const entryFeeFromSale = Math.min(entryFee || 0, netProceeds);
    const totalAllocated = entryFeeFromSale +
        (details.downsizerContribution || 0) +
        (details.partnerDownsizerContribution || 0) +
        (details.giftingAmount || 0) +
        (details.funeralBondPurchase || 0) +
        (details.purchaseAnnuity || 0) +
        (details.purchaseInvestmentProperty || 0) +
        ((details.purchaseInvestmentProperty || 0) > 0 ? (details.investmentPropertyStampDuty || 0) : 0) +
        (details.purchaseShares || 0);
    const unallocatedProceeds = netProceeds - totalAllocated;
    const clientAge = factFind?.client?.dob ? calculateAge(factFind.client.dob) : null;
    const partnerAge = factFind?.adviceFor === 'couple' && factFind?.partner?.dob ? calculateAge(factFind.partner.dob) : null;
    const isDownsizerEligible = (clientAge != null && clientAge >= 55);
    const isCouple = factFind?.adviceFor === 'couple';

    const availableForAnnuity = unallocatedProceeds + (details.purchaseAnnuity || 0);
    const rvAnnuityFundingOption = useMemo(() => ({
        id: 'bankAccounts-rv-sale-proceeds',
        assetId: 'rv-sale-proceeds',
        label: `Sale proceeds (after entry and other allocations) – ${formatCurrency(availableForAnnuity)} available`,
        type: 'bank',
        ownership: 'joint',
        available: availableForAnnuity
    }), [availableForAnnuity]);

    const rvAnnuityDetails = useMemo(() => ({
        id: 'rv-sell-annuity',
        purchaseAmount: details.purchaseAnnuity || 0,
        annuityType: details.rvAnnuityType === 'fixedTerm' ? 'fixedTerm' : details.rvAnnuityType === 'guaranteed' ? 'guaranteed' : 'lifetime',
        residualCapital: Math.min(1, Math.max(0, (details.rvAnnuityResidualPercent || 0) / 100)),
        termYears: (details.rvAnnuityType === 'fixedTerm') ? (details.rvAnnuityTermYears || null) : null,
        indexation: !!details.rvAnnuityIndexation,
        reversionary: !!details.rvAnnuityReversionary && !!partnerAge,
        fundingSource: rvAnnuityFundingOption,
        owner: details.rvAnnuityOwner || (factFind?.adviceFor === 'couple' ? 'joint' : 'client'),
        age: clientAge,
        partnerAge: partnerAge,
        incomeOverride: details.rvAnnuityIncomeOverride ?? null
    }), [details.purchaseAnnuity, details.rvAnnuityType, details.rvAnnuityResidualPercent, details.rvAnnuityTermYears, details.rvAnnuityIndexation, details.rvAnnuityReversionary, details.rvAnnuityOwner, details.rvAnnuityIncomeOverride, rvAnnuityFundingOption, factFind?.adviceFor, clientAge, partnerAge]);

    const rvSimulatedFactFindForAnnuity: FactFind = useMemo(() => {
        const data = structuredClone(factFind || {});
        if (!data.assets) data.assets = {};
        if (!data.assets.bankAccounts) data.assets.bankAccounts = [];
        const existing = data.assets.bankAccounts.find((a: DraftAsset) => a.id === 'rv-sale-proceeds');
        if (existing) existing.value = availableForAnnuity;
        else data.assets.bankAccounts.push({ id: 'rv-sale-proceeds', type: 'bankAccounts', description: 'Sale proceeds (after entry and other allocations)', value: availableForAnnuity, ownership: 'joint' });
        // Draft factFind (string-or-number fields) → real FactFind cast (see factFindTypes.ts header comment).
        return data as unknown as FactFind;
    }, [factFind, availableForAnnuity]);

    const rvAnnuitySummary = useMemo(() => {
        if (!rvAnnuityDetails.purchaseAmount) return null;
        try {
            const calculated = buildAnnuityImpact(rvSimulatedFactFindForAnnuity, rvAnnuityDetails);
            return calculated && !calculated.warnings ? summariseAnnuityImpact(calculated) : null;
        } catch { return null; }
    }, [rvSimulatedFactFindForAnnuity, rvAnnuityDetails]);

    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- kept for parity with the aged-care allocator pattern this mirrors; wired up if an annuity-editor subcomponent is added.
    const setRvAnnuityDetails = (updater: unknown) => {
        const next: Record<string, unknown> = typeof updater === 'function' ? updater(rvAnnuityDetails) : updater;
        setDetails(prev => ({
            ...prev,
            purchaseAnnuity: (next.purchaseAmount as number) || 0,
            rvAnnuityType: (next.annuityType as SellHomeDetails['rvAnnuityType']) || 'lifetime',
            rvAnnuityResidualPercent: Math.round(((next.residualCapital as number) ?? 0) * 100),
            rvAnnuityTermYears: (next.termYears as number) ?? 0,
            rvAnnuityIndexation: !!next.indexation,
            rvAnnuityReversionary: !!next.reversionary,
            rvAnnuityIncomeOverride: next.incomeOverride != null ? (next.incomeOverride as number) : undefined,
            rvAnnuityOwner: (next.owner as SellHomeDetails['rvAnnuityOwner']) || prev.rvAnnuityOwner || (isCouple ? 'joint' : 'client')
        }));
    };

    return (
        <div className="mt-4 p-4 bg-gray-50 rounded-lg border space-y-6">
            <h4 className="font-semibold text-lg">1. Calculate net proceeds from sale</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-2">
                <CurrencyInput label="Sale price ($)" value={details.salePrice ?? ''} onChange={v => handleDetailChange('salePrice', v)} className="" />
                <CurrencyInput label="Selling costs ($)" value={details.sellingCosts ?? ''} onChange={v => handleDetailChange('sellingCosts', v)} className="" />
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Net proceeds</label><p className="w-full px-4 py-2 bg-gray-200 border border-gray-300 rounded-lg font-bold">{formatCurrency(netProceeds)}</p></div>
            </div>
            <h4 className="font-semibold text-lg">2. Allocate the proceeds</h4>
            <div className="mt-2 p-3 bg-blue-50 border-l-4 border-blue-400 rounded-r-lg text-sm flex justify-between items-center">
                <span><strong className="text-blue-700">Unallocated (to bank):</strong></span>
                <span className={`font-bold text-lg ${unallocatedProceeds < 0 ? 'text-red-600' : 'text-green-600'}`}>{formatCurrency(unallocatedProceeds)}</span>
            </div>
            <div className="mt-2"><label className="block text-sm font-medium text-gray-700 mb-1">Village entry fee (from sale)</label><p className="px-3 py-2 bg-gray-100 rounded-lg font-semibold">{formatCurrency(entryFeeFromSale)}</p><p className="text-xs text-gray-500 mt-1">Uses your entry payment amount above, up to net proceeds.</p></div>
            <div className="mt-4 space-y-4">
                <AllocationInput icon={Wallet} label={details.commenceIncomeStream ? 'Downsizer to ABP' : 'Downsizer to super'} name="downsizerContribution" value={details.downsizerContribution} onChange={handleDetailChange} max={Math.min(300000, netProceeds)} disabled={!isDownsizerEligible} tooltip={strategyExplainers.downsizerToSuper(isDownsizerEligible)} />
                {factFind?.adviceFor === 'couple' && <AllocationInput icon={Wallet} label={details.commenceIncomeStream ? 'Partner downsizer to ABP' : 'Partner downsizer to super'} name="partnerDownsizerContribution" value={details.partnerDownsizerContribution} onChange={handleDetailChange} max={Math.min(300000, netProceeds)} disabled={!isDownsizerEligible} tooltip={strategyExplainers.partnerDownsizerToSuper(isDownsizerEligible)} />}
                <DownsizerRulesAndInsights factFind={factFind} />
                {isDownsizerEligible && (clientAge >= 60 || (partnerAge != null && partnerAge >= 60)) && (
                    <div className="flex items-center gap-2">
                        <input
                            type="checkbox"
                            id="rv-sell-commenceIncomeStream"
                            checked={!!details.commenceIncomeStream}
                            onChange={(e) => setDetails(prev => ({ ...prev, commenceIncomeStream: e.target.checked }))}
                        />
                        <label htmlFor="rv-sell-commenceIncomeStream" className="text-sm text-gray-700">Commence an income stream from downsizer contribution</label>
                    </div>
                )}
                <AllocationInput icon={Gift} label="Gift to loved ones" name="giftingAmount" value={details.giftingAmount} onChange={handleDetailChange} max={netProceeds} tooltip={strategyExplainers.giftToLovedOnes()} />
                <AllocationInput icon={ShieldCheck} label="Purchase funeral bond" name="funeralBondPurchase" value={details.funeralBondPurchase} onChange={handleDetailChange} max={FUNERAL_BOND_LIMIT} tooltip={strategyExplainers.funeralBond()} />
                <AllocationInput icon={TrendingUpIcon} label="Buy annuity" name="purchaseAnnuity" value={details.purchaseAnnuity} onChange={handleDetailChange} max={netProceeds} tooltip={strategyExplainers.buyAnnuity()} />
                {(details.purchaseAnnuity || 0) > 0 && (
                    <div className="ml-6 mt-2 p-4 bg-white border border-blue-200 rounded-lg space-y-4">
                        <h5 className="text-sm font-semibold text-gray-900">Annuity details</h5>
                        <p className="text-xs text-gray-600">
                            The numbers below are estimates. When you have a quote from a provider, you can enter the actual income so your plan reflects the real product.
                        </p>

                        {/* Client education: what is an annuity, pros/cons, estate */}
                        <details className="bg-slate-50 border border-slate-200 rounded-lg overflow-hidden">
                            <summary className="px-3 py-2 text-sm font-medium text-slate-800 cursor-pointer hover:bg-slate-100">What is an annuity? – pros, cons and your estate</summary>
                            <div className="px-3 pb-3 pt-1 text-xs text-slate-700 space-y-2 border-t border-slate-200">
                                <p><strong>In simple terms:</strong> You pay a lump sum to a provider and receive regular income for a set period or for life. It can help with budgeting and may reduce how much of your savings is counted for the Age Pension.</p>
                                <p><strong>Pros:</strong> Guaranteed income; no market risk on that income; can help with pension eligibility; useful to cover ongoing costs (e.g. village fees).</p>
                                <p><strong>Cons:</strong> You usually cannot get the lump sum back early; less flexibility if you later need a large amount (e.g. for aged care); no capital growth.</p>
                                <p><strong>Your estate:</strong> You choose how much (if any) is guaranteed back to your estate. That choice affects your annual income amount and what you leave to family.</p>
                            </div>
                        </details>

                        {/* Assumed income and quote override */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                <p className="text-xs font-medium text-blue-900 mb-1">Estimated annual income</p>
                                <p className="text-lg font-semibold text-blue-800">{formatCurrency(rvAnnuitySummary?.annualPayment ?? 0)}</p>
                                <p className="text-[11px] text-blue-700 mt-1">Based on your choices below. Use the override if you have a provider quote.</p>
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Annual income from your quote (optional)</label>
                                <CurrencyInput
                                    value={details.rvAnnuityIncomeOverride ?? ''}
                                    onChange={v => setDetails(prev => ({ ...prev, rvAnnuityIncomeOverride: v }))}
                                    placeholder="Enter when you have a quote"
                                    className=""
                                />
                                <p className="text-[11px] text-gray-500 mt-1">Enter the actual annual payment from your quote to update the plan. Contact an annuity provider (e.g. Challenger, Generation Life) for a quote.</p>
                            </div>
                        </div>

                        {/* Who will own the annuity? */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs text-gray-700">
                            <div>
                                <p className="font-semibold text-gray-800 mb-1">Who will own this annuity?</p>
                                <div className="space-y-1">
                                    <label className="inline-flex items-center gap-2 mr-3">
                                        <input
                                            type="radio"
                                            name="rv-annuity-owner"
                                            checked={(details.rvAnnuityOwner || (isCouple ? 'joint' : 'client')) === 'client'}
                                            onChange={() => setDetails(prev => ({ ...prev, rvAnnuityOwner: 'client' }))}
                                        />
                                        <span>{factFind?.client?.firstName || 'Client'}</span>
                                    </label>
                                    {isCouple && (
                                        <>
                                            <label className="inline-flex items-center gap-2 mr-3">
                                                <input
                                                    type="radio"
                                                    name="rv-annuity-owner"
                                                    checked={(details.rvAnnuityOwner || 'joint') === 'joint'}
                                                    onChange={() => setDetails(prev => ({ ...prev, rvAnnuityOwner: 'joint' }))}
                                                />
                                                <span>Joint</span>
                                            </label>
                                            <label className="inline-flex items-center gap-2 mr-3">
                                                <input
                                                    type="radio"
                                                    name="rv-annuity-owner"
                                                    checked={details.rvAnnuityOwner === 'partner'}
                                                    onChange={() => setDetails(prev => ({ ...prev, rvAnnuityOwner: 'partner' }))}
                                                />
                                                <span>{factFind?.partner?.firstName || 'Partner'}</span>
                                            </label>
                                        </>
                                    )}
                                </div>
                                <p className="mt-1 text-[11px] text-gray-500">Centrelink generally splits income and assets according to ownership (for example, half each for joint ownership).</p>
                            </div>
                            <div className="text-[11px] text-gray-700 space-y-1">
                                <p className="font-semibold text-gray-800">Centrelink – how it is usually assessed</p>
                                { (details.rvAnnuityType || 'lifetime') !== 'fixedTerm' ? (
                                    <>
                                        <p><strong>Lifetime annuity (post‑2019 rules):</strong></p>
                                        <ul className="list-disc list-inside space-y-1">
                                            <li><strong>Assets test:</strong> usually 60% of your purchase price is counted as an asset for the first five years, or until age 85 if that is later, and 30% after that.</li>
                                            <li><strong>Income test:</strong> usually 60% of each payment is assessed as income.</li>
                                            <li><strong>Estate:</strong> your choice about money back changes the income vs estate trade‑off. The full amount comes back only if death is within the first half of life expectancy, then less, then nothing — that is the condition for the 60%/30% rule, so the rule itself does not change.</li>
                                        </ul>
                                    </>
                                ) : (
                                    <>
                                        <p><strong>Fixed‑term annuity:</strong></p>
                                        <ul className="list-disc list-inside space-y-1">
                                            <li><strong>Assets test:</strong> Centrelink usually looks at the actual value of the annuity, which can reduce over time if you do not guarantee 100% back to your estate.</li>
                                            <li><strong>Income test:</strong> payments are split into income and a return of capital. Only the income portion is assessed.</li>
                                            <li><strong>Estate:</strong> the residual capital value at the end of the term is what is expected to be left for you or your estate.</li>
                                        </ul>
                                    </>
                                )}
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Annuity type</label>
                                <select
                                    value={details.rvAnnuityType || 'lifetime'}
                                    onChange={e => setDetails(prev => ({ ...prev, rvAnnuityType: e.target.value as SellHomeDetails['rvAnnuityType'] }))}
                                    className="w-full px-2 py-2 border border-gray-300 rounded-lg focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                >
                                    <option value="lifetime">Lifetime</option>
                                    <option value="fixedTerm">Fixed term</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Capital back to estate (%)</label>
                                <input
                                    type="number"
                                    min="0"
                                    max="100"
                                    value={details.rvAnnuityResidualPercent ?? 0}
                                    onChange={e => setDetails(prev => ({ ...prev, rvAnnuityResidualPercent: parseFloat(e.target.value) || 0 }))}
                                    className="w-full px-2 py-2 border border-gray-300 rounded-lg focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                />
                                {(details.rvAnnuityType || 'lifetime') !== 'fixedTerm' && (() => {
                                    // What that percentage actually buys, on this owner's life (annuityWords.ts).
                                    const ownerKey = details.rvAnnuityOwner || 'client';
                                    const ownerAge = ownerKey === 'partner' ? partnerAge : clientAge;
                                    const otherAge = ownerKey === 'partner' ? clientAge : partnerAge;
                                    const period = capitalAccessPeriodFor({
                                        age: ownerAge,
                                        sex: ownerKey === 'partner' ? factFind?.partner?.sex : factFind?.client?.sex,
                                        partnerAge: otherAge,
                                        partnerSex: ownerKey === 'partner' ? factFind?.client?.sex : factFind?.partner?.sex,
                                        joint: ownerKey === 'joint' || !!details.rvAnnuityReversionary,
                                    });
                                    const ownerName = ownerKey === 'partner' ? (factFind?.partner?.firstName || 'your partner') : ownerKey === 'joint' ? 'the survivor' : (factFind?.client?.firstName || 'you');
                                    return (
                                        <p className="mt-1 text-[11px] leading-relaxed text-gray-600">
                                            {capitalReturnedWords({ residualPct: Number(details.rvAnnuityResidualPercent) || 0, periodYears: period?.periodYears ?? null, sexKnown: period?.sexKnown ?? null, ownerName })}
                                        </p>
                                    );
                                })()}
                            </div>
                            { (details.rvAnnuityType || 'lifetime') === 'fixedTerm' && (
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Term (years)</label>
                                    <input
                                        type="number"
                                        min="1"
                                        value={details.rvAnnuityTermYears || ''}
                                        onChange={e => setDetails(prev => ({ ...prev, rvAnnuityTermYears: parseFloat(e.target.value) || 0 }))}
                                        className="w-full px-2 py-2 border border-gray-300 rounded-lg focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>
                            )}
                        </div>
                        <div className="flex flex-wrap gap-4 items-center text-xs text-gray-700">
                            <label className="inline-flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    checked={!!details.rvAnnuityIndexation}
                                    onChange={e => setDetails(prev => ({ ...prev, rvAnnuityIndexation: e.target.checked }))}
                                />
                                <span>Index payments each year (education only)</span>
                            </label>
                            {factFind?.adviceFor === 'couple' && (
                                <label className="inline-flex items-center gap-2">
                                    <input
                                        type="checkbox"
                                        checked={!!details.rvAnnuityReversionary}
                                        onChange={e => setDetails(prev => ({ ...prev, rvAnnuityReversionary: e.target.checked }))}
                                    />
                                    <span>Continue payments to partner after death (reversionary)</span>
                                </label>
                            )}
                        </div>

                    </div>
                )}
                <AllocationInput icon={Building} label="Buy investment property" name="purchaseInvestmentProperty" value={details.purchaseInvestmentProperty} onChange={handleDetailChange} max={netProceeds} tooltip={strategyExplainers.buyInvestmentProperty()} />
                {(details.purchaseInvestmentProperty || 0) > 0 && (
                    <div className="ml-6 pl-4 border-l-2 border-gray-200 space-y-2">
                        <CurrencyInput label="Assumed annual rental income ($)" value={details.investmentPropertyRent ?? ''} onChange={v => handleDetailChange('investmentPropertyRent', v)} className="max-w-xs" />
                        <CurrencyInput label="Assumed annual expenses ($)" value={details.investmentPropertyExpenses ?? ''} onChange={v => handleDetailChange('investmentPropertyExpenses', v)} className="max-w-xs" />
                        <p className="text-xs text-gray-600">Investment property expenses (e.g. rates, insurance, maintenance) are generally tax deductible.</p>
                        {/* The duty on the purchase comes out of the proceeds on top of the price, and sits in
                            the property's cost base (owner, 24 Sep 2026). No state, no duty — said below. */}
                        <StampDutyFields
                            subject="the investment property"
                            state={details.investmentPropertyState}
                            onStateChange={(st) => setDetails(prev => ({ ...prev, investmentPropertyState: st, ...(st ? {} : { investmentPropertyStampDuty: 0 }) }))}
                            newHomePrice={details.purchaseInvestmentProperty}
                            duty={details.investmentPropertyStampDuty}
                            onDutyChange={(d) => setDetails(prev => ({ ...prev, investmentPropertyStampDuty: d }))}
                            fieldClassName="max-w-xs"
                        />
                    </div>
                )}
                <AllocationInput icon={TrendingUpIcon} label="Buy shares" name="purchaseShares" value={details.purchaseShares} onChange={handleDetailChange} max={netProceeds} tooltip={strategyExplainers.buyShares()} />
                {/* The approach those shares are PRICED at — read by this screen since it was built,
                    offered nowhere in Manual Control until now. See NewMoneyProfileField's header. */}
                {(details.purchaseShares || 0) > 0 && (
                    <NewMoneyProfileField factFind={factFind} setFactFind={setFactFind} idPrefix="rv-sell-home" what="this money" />
                )}
                {/* Manual Control never invests money nobody committed — but it must not let a large
                    sum idle at 2% UNNOTICED either. Loud cash, one visible click: the button writes
                    the shares field (an explicit, recorded act — the allocation IS the provenance),
                    and the engine prices those shares at the client's new-money profile. */}
                {unallocatedProceeds >= 25000 && (
                    <div className="rounded-lg bg-white border border-slate-200/70 border-l-4 border-l-amber-500 px-3 py-2.5 shadow-sm flex items-center justify-between gap-3 flex-wrap">
                        <p className="text-xs text-slate-700">
                            <strong>{formatCurrency(unallocatedProceeds)}</strong> of sale proceeds will sit in the bank earning 2% interest with no growth. That&apos;s a decision too — leave it there, or invest it.
                        </p>
                        <button
                            type="button"
                            onClick={() => setDetails(prev => ({ ...prev, purchaseShares: (prev.purchaseShares || 0) + unallocatedProceeds }))}
                            className="shrink-0 px-3 py-1.5 rounded-lg bg-blue-600 text-white text-xs font-semibold hover:bg-blue-700 transition-colors"
                        >
                            Invest the remainder — {INVESTMENT_PROFILES[newMoneyProfileOf(factFind)].label}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

interface RetirementVillageExplainedProps {
  setActivePage: (page: string) => void;
  onContinue: () => void;
  rvState: string;
  setRvState: (state: string) => void;
}

/** "Is a village right for you?" — each point leads with the claim, then the detail. */
const RV_CHECKLIST_LOVES: { icon: LucideIcon; lead: string; body: React.ReactNode }[] = [
  { icon: Users, lead: 'Community on your doorstep.', body: "Neighbours at a similar stage of life, clubs and activities, and someone to notice if you don't show up for coffee." },
  { icon: Home, lead: 'A secure, low-maintenance home.', body: 'No gutters, no lawns, and help on call if you want it.' },
  { icon: Sparkles, lead: 'Money and energy freed up.', body: 'Moving out of a large family home can release both for the things you actually want to do.' },
];
const RV_CHECKLIST_WEIGH_UP: { icon: LucideIcon; lead: string; body: React.ReactNode }[] = [
  { icon: FileSignature, lead: "You're buying a contract, not just a home.", body: 'Leaving costs real money (the departure fee below), so a village suits people planning to stay.' },
  { icon: Clock, lead: 'Your exit money usually waits for a re-sale.', body: "That timing matters for your estate, and for funding aged care if it's ever needed." },
  { icon: Repeat, lead: 'A later move into aged care means moving twice.', body: 'Worth modelling both steps, which this tool can do.' },
  { icon: Scale, lead: 'Compare contracts, not brochures.', body: "Look at two or three villages' contracts and get legal advice before signing. Your state's protections are summarised further down this page." },
];

const RetirementVillageExplained = ({ setActivePage, onContinue, rvState, setRvState }: RetirementVillageExplainedProps) => {
  const [openAccordion, setOpenAccordion] = useState<string | null>(null);
  const toggleAccordion = (id: string) => setOpenAccordion(openAccordion === id ? null : id);
  const stateRules = getRetirementVillageStateRules(rvState);
  return (
    <div className="max-w-6xl mx-auto">
      <Button variant="ghost" size="sm" icon={ChevronLeft} onClick={() => setActivePage('Dashboard')} className="mb-4 -ml-2">Back to Dashboard</Button>
      <div className="mb-8">
        <div className="flex items-center gap-4 mb-4">
          <IconTile icon={Building} tone="brand" variant="solid" size="lg" className="hidden sm:inline-flex" />
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900"><GradientText>Retirement Village (Independent Living)</GradientText></h1>
        </div>
        <p className="text-lg text-slate-700 leading-relaxed mb-3">Plenty of Australians choose village living for the lifestyle: your own home without the maintenance, a ready-made community, and facilities — gardens, pools, social clubs — a short walk away. You live independently, add services like cleaning or meals only if and when you want them, and lock up and travel whenever you like. It&apos;s not aged care, and no government assessment is needed — it&apos;s a lifestyle choice with a financial contract attached, and this section helps you weigh both.</p>
        <p className="text-sm text-slate-500">Many villages provide a <strong>disclosure statement</strong> that sets out costs and contract terms. If you have or expect one, use it when modelling your options in the calculator.</p>
      </div>

      {/* The honest-decision framing lives HERE, before the fee mechanics: lifestyle upsides and the
          things worth weighing, without duplicating the cost accordion below. Collapsed by default.
          Owner, 1 Oct 2026: "it just looks bland" — each point now leads with a short bold claim and
          its own icon, in two titled panels (what residents love / what to weigh up). */}
      <details className="mb-8 rounded-2xl border border-slate-200 bg-white shadow-card group overflow-hidden">
        <summary className="cursor-pointer list-none p-5 flex items-center gap-4 [&::-webkit-details-marker]:hidden">
          <IconTile icon={Scale} tone="brand" variant="soft" size="md" className="hidden sm:inline-flex" />
          <div className="flex-1 min-w-0">
            <span className="block text-lg font-bold text-gray-900">Is a village right for you?</span>
            <span className="block text-sm text-slate-600">The honest checklist — what residents love, and what to weigh up before you sign.</span>
          </div>
          <span className="shrink-0 inline-flex items-center gap-1 rounded-full border border-slate-200 px-3 py-1 text-sm font-semibold text-blue-700">
            <span className="group-open:hidden">Show</span>
            <span className="hidden group-open:inline">Hide</span>
            <ChevronDown size={16} className="transition-transform group-open:rotate-180" aria-hidden />
          </span>
        </summary>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 px-5 pb-5">
          <section className="rounded-xl border border-emerald-200 p-4">
            <h3 className="mb-3 flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-emerald-800">
              <Heart size={16} aria-hidden /> What residents tend to love
            </h3>
            <ul className="space-y-3.5">
              {RV_CHECKLIST_LOVES.map(({ icon: Icon, lead, body }) => (
                <li key={lead} className="flex gap-3">
                  <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-emerald-600 text-white">
                    <Icon size={16} aria-hidden />
                  </span>
                  <p className="text-sm leading-relaxed text-slate-700">
                    <span className="font-semibold text-slate-900">{lead}</span> {body}
                  </p>
                </li>
              ))}
            </ul>
          </section>
          <section className="rounded-xl border border-amber-200 p-4">
            <h3 className="mb-3 flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-amber-800">
              <AlertTriangle size={16} aria-hidden /> What to weigh up before signing
            </h3>
            <ul className="space-y-3.5">
              {RV_CHECKLIST_WEIGH_UP.map(({ icon: Icon, lead, body }) => (
                <li key={lead} className="flex gap-3">
                  <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-500 text-white">
                    <Icon size={16} aria-hidden />
                  </span>
                  <p className="text-sm leading-relaxed text-slate-700">
                    <span className="font-semibold text-slate-900">{lead}</span> {body}
                  </p>
                </li>
              ))}
            </ul>
          </section>
        </div>
      </details>

      <h2 className="text-2xl font-bold text-gray-900 mb-2">Your options and what they cost</h2>
      <p className="text-base text-gray-600 mb-4">Tap each one to see how it works — the upfront entry payment, the regular fees, what happens when you leave, who owns the unit, and how it affects your Age Pension and estate.</p>
      <div className="space-y-4 mb-8">
        {RV_EXPLAIN_SECTIONS.map(fee => (
          <FeeExplanationAccordion key={fee.id} {...fee} isOpen={openAccordion === fee.id} onToggle={() => toggleAccordion(fee.id)} factFind={{}} />
        ))}
      </div>

      {/* State / territory consumer-protection. The money side (Age Pension, Rent Assistance, deeming) is
          federal and identical nationwide; what differs by state is the Retirement Villages Act — especially
          how long you or your estate may wait to be repaid after leaving, and ongoing fees after vacating. */}
      <div className="mb-8 rounded-2xl border border-slate-200 bg-white p-5 shadow-card">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div className="flex items-start gap-3">
            <IconTile icon={ShieldCheck} tone="brand" variant="soft" size="sm" className="hidden sm:inline-flex" />
            <div>
              <h2 className="text-2xl font-bold text-gray-900">The rules where you live</h2>
              <p className="text-base text-slate-600 max-w-xl">The money side (Age Pension, Rent Assistance) is the same Australia-wide. What changes by state is the consumer-protection law — most importantly, how long you (or your estate) might wait to be repaid after you leave, and how long you keep paying fees after moving out.</p>
            </div>
          </div>
          <div className="shrink-0">
            <label htmlFor="rv-state" className="block text-xs font-medium text-slate-600 mb-1">Your state or territory</label>
            <select
              id="rv-state"
              value={rvState || ''}
              onChange={(e) => setRvState && setRvState(e.target.value)}
              className="border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white min-w-[14rem]"
            >
              <option value="">Select…</option>
              {RETIREMENT_VILLAGE_STATES.map((s) => <option key={s.code} value={s.code}>{s.name}</option>)}
            </select>
          </div>
        </div>

        <div className="mt-4 space-y-3">
          <div className="rounded-lg border border-slate-200 border-l-4 border-l-blue-500 bg-slate-50 p-3">
            <div className="flex items-center justify-between gap-2 mb-1">
              <span className="text-sm font-semibold text-gray-900">Getting your money back when you leave</span>
              <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${stateRules.buyback.hasStatutoryTimeframe ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`}>
                {stateRules.buyback.hasStatutoryTimeframe ? 'Statutory timeframe' : 'Contract-based'}
              </span>
            </div>
            <p className="text-sm text-slate-700 leading-relaxed">{stateRules.buyback.summary}</p>
          </div>
          <div className="rounded-lg border border-slate-200 border-l-4 border-l-slate-400 bg-slate-50 p-3">
            <span className="block text-sm font-semibold text-gray-900 mb-1">Ongoing fees after you move out</span>
            <p className="text-sm text-slate-700 leading-relaxed">{stateRules.recurrentChargesAfterVacating}</p>
          </div>
          {stateRules.note && <p className="text-xs text-slate-500 leading-relaxed">{stateRules.note}</p>}
        </div>

        <p className="text-xs text-slate-500 mt-3 leading-relaxed">
          {stateRules.code ? <>Governed by the <strong>{stateRules.act}</strong>. </> : <>Select your state above for the rules that apply to you. </>}
          See <a href={stateRules.regulatorUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{stateRules.regulator}</a>. Your village&apos;s {(stateRules.disclosureDocument || 'disclosure statement').toLowerCase()} and contract always govern.
        </p>
      </div>

      <p className="text-xs text-slate-500 mb-6">This tool is for illustration only. Get the village&apos;s disclosure statement and independent legal and financial advice. Consumer-protection rules differ by state and territory—see &quot;Rules where you live&quot; above for the law and regulator that apply to you.</p>
      <div className="text-center">
        <Button onClick={onContinue} variant="brand" size="lg" iconRight={ArrowRight}>
          Continue to calculator
        </Button>
      </div>
    </div>
  );
};

type FundingMap = Record<string, number>;
type StrategyMode = 'manual' | 'optimised' | '';
type FundFrom = 'investment_property' | 'sell_home' | 'liquid';

interface RetirementVillageAnalyserProps {
  setActivePage: (page: string) => void;
  factFind: DraftFactFind;
  onBack: () => void;
  savedStrategies: SavedStrategy[];
  setSavedStrategies: React.Dispatch<React.SetStateAction<SavedStrategy[]>>;
  rvState: string;
  /** Persists the new-money profile answer to the client record (App owns the fact-find state). */
  setFactFind?: React.Dispatch<React.SetStateAction<DraftFactFind>>;
  /**
   * Called with each generated Statement of Advice (the strategy plus its document snapshot) so it
   * lands on the server's advice register like aged care's (owner, 2 Oct 2026). App.tsx decides where.
   */
  onSoaGenerated?: (document: Record<string, unknown>) => void;
}

const RetirementVillageAnalyser = ({ setActivePage, factFind, onBack, savedStrategies, setSavedStrategies, rvState, setFactFind, onSoaGenerated }: RetirementVillageAnalyserProps) => {
  // Unit growth 3%/yr is a conservative starting estimate the adviser can overwrite. Years in
  // village deliberately stays 0 ("not modelling an exit"): the projection engine CLAMPS a stay
  // longer than the projection window to its final year (resolveExitYear in the village plugin,
  // "preserved rather than endorsed") — a defaulted 10-year stay in a 5-year projection modelled
  // every resident LEAVING in year 5, entitlement and all, inflating every mover's legacy by ~the
  // exit entitlement and poisoning the suggestion-engine rankings. Until the clamp's semantics are
  // fixed engine-side, an exit is modelled only when the adviser explicitly enters a residency.
  const [villageInputs, setVillageInputs] = useState<VillageInputs>({ entryFee: 0, ongoingWeeklyOrMonthly: 0, ongoingFrequency: 'monthly', dmfPctPerYear: 0, dmfCapYears: 0, yearsResidency: 0, growthRatePct: 3, capitalGainShareToResident: 0.5, reinstatementCap: 0 });
  const [strategyName, setStrategyName] = useState('');
  // '' until the user chooses — the mode choice is the FIRST act, matching the aged care and SAH
  // modules (owner-caught here too, 2026-08-25: Manual Control defaulting to on skipped the choice).
  // Restoring a saved strategy still lands in manual (see the restore handler).
  const [strategyMode, setStrategyMode] = useState<StrategyMode>('');
  /*
   * THE THREE STEPS (owner, 24 Sep 2026: "replicate the UI and functionality as much as possible
   * as to what you did with the aged care suggestion engine"). 1: the village contract and the
   * move-in date. 2: where the client stands STAYING PUT — the one baseline. 3: the two ways to fund
   * the move, with the plan summary beside them. The strip is a map, not a gate.
   */
  const [step, setStep] = useState<AnalyserStep>(1);
  const goToStep = (next: AnalyserStep) => { setStep(next); scrollAppToTop(); };
  const [activeSavedStrategyId, setActiveSavedStrategyId] = useState<string | null>(null);
  const [showCompareModal, setShowCompareModal] = useState(false);
  /** "Understanding your plan" — the full recap, opened from the plan summary. */
  const [showRvExplanation, setShowRvExplanation] = useState(false);
  const [showStrategyOptions, setShowStrategyOptions] = useState(true);
  const [showOtherStrategies, setShowOtherStrategies] = useState(false);
  const [otherStrategies, setOtherStrategies] = useState<OtherStrategies>({
    gifting: false,
    giftingAmount: 0,
    giftingSource: 'bankAccounts',
    funeralBonds: false,
    funeralBondAmount: 0,
    funeralBondSource: 'bankAccounts',
    annuity: false,
  });
  const [annuityRecord, setAnnuityRecord] = useState<AnnuityDetails | null>(null);
  const [rvOverallStrategy, setRvOverallStrategy] = useState<RvOverallStrategy>({ rentHome: false, downsizeHome: false, sellInvestmentProperty: false });
  const [rvInvestmentPropertyDetails, setRvInvestmentPropertyDetails] = useState<RvInvestmentPropertyDetails>(() => {
    const first = factFind?.assets?.investmentProperty?.[0];
    const val = first?.value || 0;
    return {
      selectedPropertyId: first?.id ?? null,
      salePrice: val,
      sellingCosts: val * 0.025,
      costBase: first?.costBase ?? 0,
      entryFeeAllocation: 0,
      downsizerContribution: 0,
      partnerDownsizerContribution: 0,
      commenceIncomeStream: false,
      giftingAmount: 0,
      funeralBondPurchase: 0
    };
  });
  const [sellHomeDetails, setSellHomeDetails] = useState<SellHomeDetails>({
    salePrice: n(factFind?.home?.value),
    sellingCosts: n(factFind?.home?.value) * 0.025,
    downsizerContribution: 0,
    partnerDownsizerContribution: 0,
    commenceIncomeStream: false,
    giftingAmount: 0,
    funeralBondPurchase: 0,
    purchaseAnnuity: 0,
    purchaseInvestmentProperty: 0,
    investmentPropertyRent: 0,
    investmentPropertyExpenses: 0,
    investmentPropertyState: rvState || '',
    investmentPropertyStampDuty: 0,
    purchaseShares: 0
  });
  const [rentDetails, setRentDetails] = useState<RentDetails>(() => ({ rentAmount: n(factFind?.home?.likelyRent), rentExpenses: n(factFind?.home?.likelyExpenses), partnerRent: 0 }));
  // Keep rent assumptions in sync with Client Profile (fact find) when user hasn't entered values yet
  useEffect(() => {
    if (!factFind?.home) return;
    const lr = n(factFind.home.likelyRent);
    const le = n(factFind.home.likelyExpenses);
    if ((lr > 0 || le > 0) && rentDetails.rentAmount === 0 && rentDetails.rentExpenses === 0) {
      setRentDetails(prev => ({ ...prev, rentAmount: lr || 0, rentExpenses: le || 0 }));
    }
  }, [factFind?.home?.likelyRent, factFind?.home?.likelyExpenses, rentDetails.rentAmount, rentDetails.rentExpenses]);
  const [entryFeeFunding, setEntryFeeFunding] = useState<FundingMap>({});
  const [entryFeeCgtOverrides, setEntryFeeCgtOverrides] = useState<Record<string, number | null>>({});
  const [localLegacyYears, setLocalLegacyYears] = useState(5);
  // Suggestion Engine state (Task #3): computed off-thread-of-render so the loader paints first.
  const [seLoading, setSeLoading] = useState(false);
  const [rvSuggestions, setRvSuggestions] = useState<{ strategies: EvaluatedStrategy[]; recommended: RecommendedStrategyEntry[]; stayHome?: EvaluatedStrategy | null } | null>(null);
  const [seDeepDive, setSeDeepDive] = useState<{ projections: RvProjectionRow[]; name: string } | null>(null);
  // "What could go wrong" for one engine card (StrategyRisksPanel).
  const [riskStrategy, setRiskStrategy] = useState<RvEvaluatedStrategy | null>(null);

  /**
   * The Statement of Advice for the selected engine strategy (owner, 2 Oct 2026). Built from the
   * figures already on screen (rvSoa.ts), opened and downloaded like aged care's, and handed to App
   * so it lands on the advice register with its document snapshot.
   */
  const generateRvSoa = (strategy: RvEvaluatedStrategy) => {
    const snapshot = buildRetirementVillageSoaSnapshot({
      strategy,
      all: (rvSuggestions?.strategies || []) as RvEvaluatedStrategy[],
      leaders: visibleSeCards.map((c) => ({ strategy: c.strategy, badges: c.badges })),
      baseline: (rvSuggestions?.stayHome as RvEvaluatedStrategy | null | undefined) ?? null,
      factFind,
      summary,
      villageInputs,
      legacyYears: Number(localLegacyYears) || 5,
    });
    openModuleSoa(snapshot);
    onSoaGenerated?.({ id: strategy.id, name: strategy.name, type: 'retirement_village', source: 'suggestion_engine', strategy, soaSnapshot: snapshot });
  };
  const [selectedSeId, setSelectedSeId] = useState<string | null>(null); // selected SE strategy → drives the narrative + Financial Impact panels
  /*
   * THE STRATEGY ANALYSIS SETUP (owner, 24 Sep 2026). Opens once per visit when the Suggestion
   * Engine is chosen, and again from "Adjust setup". Its answers are saved on the client record
   * (goals.rvPurchaseState / rvAnnuityShareCapPct / rvCashflowTarget), so a returning adviser sees
   * their own choices rather than the defaults. While it is open the engine waits: one run on the
   * answers, not a run on the defaults and then another.
   */
  const [rvSetup, setRvSetup] = useState<RvEngineSetup>(() => rvEngineSetupFromGoals(factFind, rvState));
  const [rvSetupOpen, setRvSetupOpen] = useState(false);
  const [rvSetupSeen, setRvSetupSeen] = useState(false);
  const rvNetFreedAfterEntry = useMemo(() => {
    const homeValue = n(factFind?.home?.value);
    if (!(homeValue > 0)) return 0;
    return Math.max(0, homeValue - Math.round(homeValue * 0.025) - (n(villageInputs.entryFee) || 0));
  }, [factFind?.home?.value, villageInputs.entryFee]);
  // Entry fee funding: only sell IP or sell/downsize home use property; otherwise liquid (including when renting the home for cashflow).
  const fundFrom: FundFrom = rvOverallStrategy.sellInvestmentProperty ? 'investment_property' : rvOverallStrategy.downsizeHome ? 'sell_home' : 'liquid';
  const EMERGENCY_RESERVE = getRecommendedEmergencyFund(factFind); // 6 months of living expenses
  /*
   * WHAT THE PROPERTY PAYS, AND WHAT SAVINGS MUST (owner-caught, 24 Sep 2026). On the sell-a-property
   * routes the shortfall was only ever computed for the savings route, so an $800,000 contribution
   * against $651,966 of net proceeds read "paid in full" and offered nowhere to allocate the other
   * $148,034. The property's share is what its sale nets, capped at the contribution; the rest is
   * the target the savings allocator below is asked for, and what the engine tops up in the same
   * order (shared topUpEntryFeeFromLiquid).
   */
  const propertyTowardEntry: number = useMemo(() => {
    const entryFee = villageInputs.entryFee;
    if (!(entryFee > 0)) return 0;
    if (fundFrom === 'investment_property') {
      const list = factFind?.assets?.investmentProperty || [];
      const ip = list.find((p) => String(p.id) === String(rvInvestmentPropertyDetails.selectedPropertyId)) || list[0];
      if (!ip) return 0;
      const { netProceeds } = computeInvestmentPropertyNetForAmount(factFind as unknown as FactFind, ip as unknown as FactFindAsset, n(ip.value));
      return Math.min(entryFee, Math.max(0, n(rvInvestmentPropertyDetails.entryFeeAllocation)), netProceeds);
    }
    if (fundFrom === 'sell_home') {
      return Math.min(entryFee, Math.max(0, n(sellHomeDetails.salePrice) - n(sellHomeDetails.sellingCosts)));
    }
    return 0;
  }, [fundFrom, villageInputs.entryFee, factFind, rvInvestmentPropertyDetails.selectedPropertyId, rvInvestmentPropertyDetails.entryFeeAllocation, sellHomeDetails.salePrice, sellHomeDetails.sellingCosts]);
  /** What the savings allocator is asked for: the whole contribution, or what the sale leaves. */
  const liquidTargetForEntryFee = fundFrom === 'liquid' ? villageInputs.entryFee : Math.max(0, villageInputs.entryFee - propertyTowardEntry);
  const defaultEntryFeeAllocation: FundingMap = useMemo(() => {
    const entryFee = liquidTargetForEntryFee;
    if (entryFee <= 0 || !factFind?.assets) return {};
    const order = ['bankAccounts', 'termDeposits', 'accountBasedPensions', 'shares'];
    let remaining = entryFee;
    const out: FundingMap = {};
    order.forEach(type => {
      if (remaining <= 0 || !factFind.assets![type]) return;
      if (type === 'bankAccounts') {
        const bankAccounts = factFind.assets!.bankAccounts || [];
        const totalBank = bankAccounts.reduce((s, a) => s + n(a.value), 0);
        const availableFromBank = Math.max(0, totalBank - EMERGENCY_RESERVE);
        let takenFromBank = 0;
        bankAccounts.forEach(asset => {
          if (remaining <= 0 || takenFromBank >= availableFromBank) return;
          const assetId = `bankAccounts-${asset.id}`;
          const val = n(asset.value);
          const canTake = Math.min(val, remaining, availableFromBank - takenFromBank);
          if (canTake > 0) {
            out[assetId] = canTake;
            remaining -= canTake;
            takenFromBank += canTake;
          }
        });
        return;
      }
      (factFind.assets![type] || []).forEach((asset: DraftAsset) => {
        if (remaining <= 0) return;
        const assetId = `${type}-${asset.id}`;
        const val = n(asset.value);
        const take = Math.min(val, remaining);
        if (take > 0) { out[assetId] = take; remaining -= take; }
      });
    });
    /*
     * NO PARTIAL PRE-FILL WHEN SAVINGS CANNOT COVER IT (owner, 2026-09-01).
     *
     * `remaining > 0` here means the draw order ran out of liquid assets before it reached the entry
     * contribution — the savings genuinely cannot fund this move. Handing back the partial
     * allocation anyway put every slider at maximum, drew down the account-based pension by default,
     * and then told the client on screen that none of it would work and the real decision was
     * elsewhere. They had to read a wall of reasoning about a plan the tool already knew was
     * impossible, and then undo it.
     *
     * A village contribution has to be paid in full, so a partial allocation is not a partial plan —
     * it is no plan. Returning nothing leaves the sliders at zero and lets the screen lead with the
     * routes that CAN fund it (selling or renting the home, selling an investment property), which
     * is where the decision actually is.
     */
    if (remaining > 0) return {};
    return out;
  }, [liquidTargetForEntryFee, factFind?.assets]);
  // Resolved by the SAME function the allocator card renders from: the user's figure for any asset
  // they have touched, the default for every asset they have not. Previously this took the user's
  // map wholesale once it had any key at all, so dragging one slider dropped the other assets'
  // defaults from the map handed to the ENGINE while the card still showed them — the projections
  // modelled a fraction of the entry fee the client could see was fully funded.
  const effectiveEntryFeeFunding: FundingMap = useMemo(
    () => resolveRvEntryFeeFunding(factFind, entryFeeFunding, defaultEntryFeeAllocation),
    [factFind, entryFeeFunding, defaultEntryFeeAllocation],
  );
  const totalTowardEntryFee = useMemo(
    () =>
      computeRvEntryFeeFundingTotals(factFind as unknown as FactFind, effectiveEntryFeeFunding, entryFeeCgtOverrides)
        .netTotal,
    [effectiveEntryFeeFunding, entryFeeCgtOverrides, factFind]
  );
  const entryFeeAmount = villageInputs.entryFee;
  // On every route: what neither the sale nor the allocated savings cover.
  const entryFeeShortfall = liquidTargetForEntryFee > 0 ? Math.max(0, liquidTargetForEntryFee - totalTowardEntryFee) : 0;
  const rvCoreStrategy = useMemo(
    () => ({
      fundFrom,
      rentHome: rvOverallStrategy.rentHome,
      sellHomeDetails: fundFrom === 'sell_home' ? sellHomeDetails : undefined,
      rentDetails: rvOverallStrategy.rentHome ? rentDetails : undefined,
      // On every route — on a property route this is the remainder the sale did not cover.
      entryFeeFunding: effectiveEntryFeeFunding,
      entryFeeCgtOverrides,
      investmentPropertyDetails: fundFrom === 'investment_property' ? rvInvestmentPropertyDetails : undefined,
    }),
    [fundFrom, rvOverallStrategy.rentHome, sellHomeDetails, rentDetails, effectiveEntryFeeFunding, entryFeeCgtOverrides, rvInvestmentPropertyDetails],
  );

  const liquidFactFindAfterVillage: DraftFactFind = useMemo(
    () => buildSimulatedFactFindForVillageStrategy(factFind as unknown as FactFind, rvCoreStrategy, villageInputs).factFind,
    [factFind, rvCoreStrategy, villageInputs],
  );

  /**
   * The shared debt strategy — see the Aged Care module. Handed to the ENGINE via the strategy
   * options rather than applied here: the village projections' own year-1 financials are what feed
   * this page's after-position and impact panel, so the one wiring point keeps the projections, the
   * panel and the legacy estimate agreeing about whether the debt exists. The engine performs the
   * payoff at the projection's start — a lender takes the money any day.
   */
  /**
   * When the client moves into the village, blank = model it from today. A case fact, asked with the
   * village costs and threaded to the manual projection AND every Suggestion Engine candidate — the
   * two modes must never model the same move on different timing.
   */
  const [villageEntryDate, setVillageEntryDate] = useState<string>('');
  const villageEntryDateInPast = villageEntryDate !== '' && villageEntryDate < new Date().toISOString().slice(0, 10);
  const effectiveVillageEntryDate = villageEntryDateInPast ? '' : villageEntryDate;

  const [debtPayments, setDebtPayments] = useState<Record<string, number>>({});
  const [debtFunding, setDebtFunding] = useState<Record<string, number>>({});
  const [debtRepaymentBasis, setDebtRepaymentBasis] = useState<'keep_repayment' | 'lower_repayment'>('keep_repayment');
  const debtPayoffPlan = useMemo<DebtPayoff | null>(() => {
    const payments = Object.fromEntries(Object.entries(debtPayments).filter(([, amount]) => (Number(amount) || 0) > 0));
    if (Object.keys(payments).length === 0) return null;
    const funding = Object.fromEntries(Object.entries(debtFunding).filter(([, amount]) => (Number(amount) || 0) > 0));
    return { payments, funding, repaymentBasis: debtRepaymentBasis };
  }, [debtPayments, debtFunding, debtRepaymentBasis]);

  const rvStrategyOptions: Record<string, unknown> = useMemo(() => {
    const hasOther =
      otherStrategies.gifting || otherStrategies.funeralBonds || otherStrategies.annuity;
    return {
      inflationAssumptionPct: DEFAULT_INFLATION_ASSUMPTION_PCT,
      otherStrategies: hasOther ? otherStrategies : undefined,
      retirementVillage: {
        ...rvCoreStrategy,
        otherStrategies: hasOther ? otherStrategies : undefined,
        annuityRecord: otherStrategies.annuity ? annuityRecord : null,
      },
      ...(debtPayoffPlan ? { debtPayoff: debtPayoffPlan } : {}),
      ...(effectiveVillageEntryDate ? { villageEntryDate: effectiveVillageEntryDate } : {}),
    };
  }, [rvCoreStrategy, otherStrategies, annuityRecord, debtPayoffPlan, effectiveVillageEntryDate]);
  const summary: VillageCostSummary = useMemo(() => getVillageCostSummary(villageInputs), [villageInputs]);

  const [projections, setProjections] = useState<RvProjectionRow[]>([]);
  useEffect(() => {
    let cancelled = false;
    // The payout choice rides with the village inputs (the server builds the moved balance sheet from them).
    calculationApi.runRetirementVillageProjections(factFind, rvStrategyOptions, { ...villageInputs, oldUnitPaysAtMove: rvSetup.oldUnitPaysAtMove !== false }, localLegacyYears).then((proj: RvProjectionRow[]) => {
      if (!cancelled) setProjections(proj);
    });
    return () => { cancelled = true; };
  }, [factFind, rvStrategyOptions, villageInputs, localLegacyYears, rvSetup.oldUnitPaysAtMove]);

  const [legacyEstimate, setLegacyEstimate] = useState(0);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (projections.length === 0) { setLegacyEstimate(0); return; }
      const lastRow = projections[localLegacyYears - 1];
      if (lastRow?.netWorth != null) { setLegacyEstimate(lastRow.netWorth); return; }
      if (lastRow?.factFind) {
        const fin = await calculationApi.calculateFinancials(lastRow.factFind) as FinancialPosition;
        if (!cancelled) setLegacyEstimate(fin.netWorth);
        return;
      }
      setLegacyEstimate(0);
    })();
    return () => { cancelled = true; };
  }, [projections, localLegacyYears]);

  const [initialFinancials, setInitialFinancials] = useState<FinancialPosition | null>(null);
  useEffect(() => {
    let cancelled = false;
    calculationApi.calculateFinancials(factFind).then((r: FinancialPosition) => { if (!cancelled) setInitialFinancials(r); });
    return () => { cancelled = true; };
  }, [factFind]);

  const simulatedFinancialsRaw = projections.length > 0 ? projections[0]!.financials : null;
  /**
   * With a dated entry, year 1 of the projection is the PRE-entry year — the client still at home —
   * so the impact panel's "after" must come from the plan-in-place position instead (the moved
   * fact-find, assessed normally). Undated, this is exactly the year-1 financials it always was.
   */
  const [villageAfterFinancials, setVillageAfterFinancials] = useState<FinancialPosition | null>(null);
  useEffect(() => {
    if (!effectiveVillageEntryDate) { setVillageAfterFinancials(null); return undefined; }
    let cancelled = false;
    // Assessed WITH the move's means-test options (rent-the-home income, kept-home assessability) —
    // the same shared mapping the server's projection run applies, so the plan-in-place position and
    // the projections can never disagree about what the move does to the tests.
    const meansOptions = buildVillageMeansAssessmentOptions(rvCoreStrategy, villageInputs, liquidFactFindAfterVillage as unknown as FactFind);
    calculationApi.calculateFinancials(liquidFactFindAfterVillage, meansOptions).then((r: FinancialPosition) => { if (!cancelled) setVillageAfterFinancials(r); });
    return () => { cancelled = true; };
  }, [effectiveVillageEntryDate, liquidFactFindAfterVillage, rvCoreStrategy, villageInputs]);
  const simulatedFinancials = effectiveVillageEntryDate ? (villageAfterFinancials ?? simulatedFinancialsRaw) : simulatedFinancialsRaw;
  const [showProjectionsModal, setShowProjectionsModal] = useState(false);
  /** What "See the projections" opens: the plan, or staying put (step 2 and the rail before a plan is named). */
  const [projectionsSource, setProjectionsSource] = useState<'plan' | 'stayPut'>('plan');
  /*
   * THE FINANCIAL-YEAR / CALENDAR-YEAR TOGGLE (owner, 23 Sep 2026: missing here, present in Aged
   * Care). It is not a display filter — the engine PROJECTS the year it REPORTS, so the rows have to
   * be cut again. That second run is what the shared modal gates the toggle on, which is why passing
   * `onPeriodModeChange` is the whole of the change. Fetched only while the modal is open, so a
   * remembered 'calendar_year' does not fire a second run on every page load, and the mode is the
   * modal's own sticky preference, shared with Aged Care.
   */
  const [modalPeriodMode, setModalPeriodMode] = useState<ProjectionsModalPeriodMode>(readPeriodMode);
  const [calendarYearProjections, setCalendarYearProjections] = useState<RvProjectionRow[] | null>(null);
  useEffect(() => {
    if (modalPeriodMode !== 'calendar_year') { setCalendarYearProjections(null); return undefined; }
    if (!showProjectionsModal) return undefined;
    let cancelled = false;
    // Cleared first so a changed input re-shows the hourglass rather than leaving the previous run's
    // rows on screen as though they were current.
    setCalendarYearProjections(null);
    calculationApi.runRetirementVillageProjections(
      factFind,
      { ...rvStrategyOptions, projectionPeriodMode: 'calendar_year' },
      villageInputs,
      localLegacyYears,
    ).then((proj: RvProjectionRow[]) => { if (!cancelled) setCalendarYearProjections(proj); });
    return () => { cancelled = true; };
  }, [showProjectionsModal, modalPeriodMode, factFind, rvStrategyOptions, villageInputs, localLegacyYears]);

  /** First row unchanged; adds fyFullYearView for dual-world FY blend (financial_year mode only). */
  const [projectionsForModal, setProjectionsForModal] = useState<RvProjectionRow[]>([]);
  useEffect(() => {
    if (!projections?.length) { setProjectionsForModal(projections); return undefined; }
    const mode = (rvStrategyOptions.projectionPeriodMode as string | undefined) ?? 'financial_year';
    if (mode === 'strategy_year') { setProjectionsForModal(projections); return undefined; }

    let cancelled = false;
    calculationApi.computeYearOneFullAustralianFyBlendRetirementVillage({
      baselineFactFind: factFind,
      strategyOptionsStrategy: rvStrategyOptions,
      villageInputsStrategy: villageInputs,
      strategyYearOneProjection: projections[0],
      initialFinancials,
      currentFinancials: simulatedFinancials,
      options: {},
    }).then((fyView: FyBlendView) => {
      if (!cancelled) setProjectionsForModal(projections.map((row, i) => (i === 0 ? { ...row, fyFullYearView: fyView } : row)));
    });
    return () => { cancelled = true; };
  }, [projections, rvStrategyOptions, villageInputs, factFind, initialFinancials, simulatedFinancials]);
  const noVillageScenario = (summary.entryFee === 0 && summary.annualOngoing === 0);
  const investmentPropertySaleSummary = useMemo(() => {
    if (fundFrom !== 'investment_property' || !rvInvestmentPropertyDetails || !factFind?.assets?.investmentProperty?.length) return null;
    const ip = factFind.assets.investmentProperty.find(p => String(p.id) === String(rvInvestmentPropertyDetails.selectedPropertyId)) || factFind.assets.investmentProperty[0]!;
    if (!ip) return null;
    const fullValue = n(ip.value);
    const { netProceeds, cgt } = computeInvestmentPropertyNetForAmount(factFind as unknown as FactFind, ip as unknown as FactFindAsset, fullValue);
    return { netProceeds, estimatedCGT: cgt };
  }, [fundFrom, rvInvestmentPropertyDetails, factFind]);
  const totalEntryFeeCgtFromLiquidIp = useMemo(() => {
    if (fundFrom !== 'liquid' || !effectiveEntryFeeFunding || !factFind?.assets?.investmentProperty) return 0;
    let total = 0;
    Object.entries(effectiveEntryFeeFunding).forEach(([assetId, amount]) => {
      if (!assetId.startsWith('investmentProperty-') || !n(amount)) return;
      const idRaw = assetId.replace(/^investmentProperty-/, '');
      const asset = factFind.assets!.investmentProperty!.find(a => String(a.id) === idRaw);
      if (asset) total += computeInvestmentPropertyNetForAmount(factFind as unknown as FactFind, asset as unknown as FactFindAsset, Math.min(n(amount), n(asset.value))).cgt;
    });
    return total;
  }, [fundFrom, effectiveEntryFeeFunding, factFind]);
  const entryFeeCgtForModal = (investmentPropertySaleSummary?.estimatedCGT ?? 0) + (totalEntryFeeCgtFromLiquidIp ?? 0);
  const [rvImpactBreakdowns, setRvImpactBreakdowns] = useState<{ agePension: BreakdownRow[]; cashFlow: BreakdownRow[]; netWorth: BreakdownRow[]; legacy: BreakdownRow[] }>({ agePension: [], cashFlow: [], netWorth: [], legacy: [] });
  useEffect(() => {
    let cancelled = false;
    (async () => {
    const fmt = (v: unknown): string => (v == null || v === '' || isNaN(Number(v))) ? '$0' : '$' + Number(v).toLocaleString('en-AU', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
    const y0 = projections[0];
    const lastProj = projections[localLegacyYears - 1];
    const agePension: BreakdownRow[] = (y0?.financials?.socialSecurityBreakdown && Array.isArray(y0.financials.socialSecurityBreakdown) && y0.financials.socialSecurityBreakdown.length > 0)
      ? y0.financials.socialSecurityBreakdown
      : (initialFinancials?.socialSecurityBreakdown && Array.isArray(initialFinancials.socialSecurityBreakdown))
        ? initialFinancials.socialSecurityBreakdown
        : [{ label: 'Age Pension (p.a.)', value: fmt(y0?.financials?.socialSecurity ?? initialFinancials?.socialSecurity) }];
    const ipCgt = (investmentPropertySaleSummary?.estimatedCGT ?? 0) + (totalEntryFeeCgtFromLiquidIp ?? 0);
    // CGT is NOT a cashflow item. Under Option A (net proceeds pool) it is deducted from the sale
    // proceeds before they are allocated, so it reduces net worth at settlement and never reaches
    // `netCashflow`. It used to be emitted as a "Less:" row ABOVE the total, which made the rows
    // fail to reconcile against the stated net cashflow and read as though cashflow bore the tax.
    // It now sits below the total, as reference only.
    const cashFlow: BreakdownRow[] = (y0?.financials?.cashFlowBreakdown && Array.isArray(y0.financials.cashFlowBreakdown))
      ? [
          ...y0.financials.cashFlowBreakdown,
          ...(summary.annualOngoing > 0 ? [{ label: '--- Village costs ---', value: '' }, { label: 'Less: Village ongoing fees (p.a.)', value: -summary.annualOngoing }] : []),
          { label: '--- Net cashflow (Year 1) ---', value: '' },
          { label: 'Net cashflow', value: y0?.netCashflow ?? 0 },
          ...(ipCgt > 0
            ? [
                { label: '--- Not in cashflow ---', value: '' },
                { label: 'CGT on investment property sale (paid from sale proceeds)', value: fmt(ipCgt) },
              ]
            : []),
        ]
      : [{ label: 'Net cashflow (Year 1)', value: fmt(y0?.netCashflow) }];
    type AssetsMapLike = Record<string, DraftAsset[] | undefined>;
    const netWorthFromAssets = (formDataOrAssets: DraftFactFind | AssetsMapLike | null | undefined): BreakdownRow[] => {
      const assets: AssetsMapLike | undefined = (formDataOrAssets as DraftFactFind)?.assets || (formDataOrAssets as AssetsMapLike | undefined);
      if (!assets) return [];
      const types = ['bankAccounts', 'termDeposits', 'shares', 'accountBasedPensions'];
      const out: BreakdownRow[] = [];
      types.forEach(t => {
        const arr = assets[t] || [];
        if (t === 'bankAccounts' && arr.some(a => a.id === 'rv-sell-residual')) {
          const residualEntry = arr.find(a => a.id === 'rv-sell-residual');
          const residualVal = n(residualEntry?.value);
          const otherBank = arr.filter(a => a.id !== 'rv-sell-residual').reduce((s, a) => s + n(a.value), 0);
          if (otherBank > 0) out.push({ label: 'Bank accounts', value: fmt(otherBank) });
          if (residualVal > 0) out.push({ label: 'Residual from home sale (to bank)', value: fmt(residualVal) });
        } else {
          const sum = arr.reduce((s, a) => s + n(a.value), 0);
          if (sum > 0) out.push({ label: (t === 'bankAccounts' ? 'Bank accounts' : t === 'termDeposits' ? 'Term deposits' : t === 'accountBasedPensions' ? 'Account-based pensions' : 'Shares'), value: fmt(sum) });
        }
      });
      const total = out.reduce((s, o) => s + (typeof o.value === 'number' ? o.value : parseFloat(String(o.value).replace(/[$,]/g, '')) || 0), 0);
      if (out.length) out.push({ label: '--- Total (liquid) ---', value: '' }, { label: 'Total', value: fmt(total) });
      return out;
    };
    const netWorth = y0?.factFind ? netWorthFromAssets(y0.factFind) : netWorthFromAssets(factFind);
    const legacyAssets = lastProj?.factFind?.assets;
    const legacyBreakdown: BreakdownRow[] = legacyAssets
      ? [
          ...netWorthFromAssets(lastProj!.factFind),
          ...((lastProj?.exitProceeds ?? 0) > 0 ? [{ label: '--- Village exit ---', value: '' }, { label: 'Exit entitlement received', value: fmt(lastProj!.exitProceeds) }] : [])
        ]
      : [{ label: 'Legacy estimate', value: fmt(legacyEstimate) }];
    let netWorthFinal = netWorth;
    if (!netWorth.length) {
      const fallbackNetWorth = y0?.factFind ? (await calculationApi.calculateFinancials(y0.factFind) as FinancialPosition).netWorth : initialFinancials?.netWorth;
      netWorthFinal = [{ label: 'Net Worth (Year 1)', value: fmt(fallbackNetWorth) }];
    }
    if (!cancelled) setRvImpactBreakdowns({ agePension, cashFlow, netWorth: netWorthFinal, legacy: legacyBreakdown });
    })();
    return () => { cancelled = true; };
  }, [projections, summary.entryFee, summary.annualOngoing, localLegacyYears, initialFinancials, factFind, legacyEstimate, investmentPropertySaleSummary, totalEntryFeeCgtFromLiquidIp]);
  const legacyEstimateDisplay = legacyEstimate;

  const [netWorthAfterDisplay, setNetWorthAfterDisplay] = useState<number | undefined>(0);
  useEffect(() => {
    // The engine's own rows now carry the accrued exit entitlement in net worth (the village plugin
    // values the contract each resident-year — the refundable-RAD principle), so this reads the
    // engine's figure directly. The old path recomputed net worth from the raw fact-find (which
    // cannot know about the contract) and added a capped entitlement back on — a UI-side patch that
    // would now double count.
    if (noVillageScenario) { setNetWorthAfterDisplay(initialFinancials?.netWorth); return; }
    /*
     * The tile says "TODAY", so it must show the position the day the plan is done — not year 1's
     * CLOSING figure, which is what it used to read (owner-caught, 2026-08-26: "I'm not clear how
     * the net worth has gone up from $2,365,000 to $2,369,997 given we started with the same money
     * and have incurred selling costs of $50k"). It had gone up because a full year of investment
     * growth was folded into a number labelled "today", masking the immediate cost of the move.
     *
     * `openingNetWorth` is that day-one position — the moved balance sheet including the refundable
     * entry contribution — so the tile now shows the real immediate effect. On the owner's scenario
     * that is $2,365,000 -> $2,315,000: down by the selling costs, and nothing else, because paying
     * an entry contribution is an asset swap rather than a loss.
     */
    const dayOne = projections[0]?.openingNetWorth;
    setNetWorthAfterDisplay(typeof dayOne === 'number' ? dayOne : initialFinancials?.netWorth);
  }, [noVillageScenario, projections, initialFinancials]);

  /**
   * The STAY-PUT estate — what this client would be worth at the horizon if they did nothing.
   *
   * Owner-caught (2026-08-26): the plan summary compared the projected estate against TODAY'S net
   * worth, while the Suggestion Engine compares it against the stay-put projection. Those are not
   * the same benchmark and they can disagree about the direction: a plan can leave more than the
   * client is worth today (five years of growth will do that on its own) while leaving materially
   * LESS than simply staying home would have. Measuring the move against today's wealth credits the
   * move with growth it did not cause, which is exactly the wrong way for a comparison to be wrong.
   *
   * One projection, the same call the engine's own baseline candidate makes: the village zeroed out.
   */
  /*
   * Kept WHOLE, not just its last row (24 Sep 2026): the stay-put projection is now the position
   * step 2 shows, the runway the rail carries until a plan is chosen, and what "See the projections"
   * opens in that state — one run, read everywhere staying put is quoted. Independent of the village
   * inputs: the village is zeroed out of it by definition.
   */
  const [stayPutProjections, setStayPutProjections] = useState<RvProjectionRow[]>([]);
  useEffect(() => {
    let cancelled = false;
    calculationApi
      .runRetirementVillageProjections(
        factFind,
        { inflationAssumptionPct: DEFAULT_INFLATION_ASSUMPTION_PCT, retirementVillage: {} },
        { entryFee: 0, ongoingWeeklyOrMonthly: 0, ongoingFrequency: 'monthly', dmfPctPerYear: 0, dmfCapYears: 0, yearsResidency: 0, growthRatePct: 0, capitalGainShareToResident: 0.5, reinstatementCap: 0 },
        localLegacyYears,
      )
      .then((rows: RvProjectionRow[]) => { if (!cancelled) setStayPutProjections(rows || []); })
      .catch(() => { if (!cancelled) setStayPutProjections([]); });
    return () => { cancelled = true; };
  }, [factFind, localLegacyYears]);
  const manualBaselineLegacy: number | null = useMemo(() => {
    const last = stayPutProjections[localLegacyYears - 1] ?? stayPutProjections[stayPutProjections.length - 1];
    return last?.netWorth ?? null;
  }, [stayPutProjections, localLegacyYears]);

  const saveStrategy = (name: string) => {
    const trimmed = (name || strategyName || '').trim();
    if (!trimmed) {
      notify('Please enter a strategy name');
      return;
    }
    const id = `rv-${Date.now()}`;
    const y0 = projections[0];
    setSavedStrategies((prev) => [
      ...(prev || []),
      {
        id,
        name: trimmed,
        type: 'retirement_village',
        inputs: {
          villageInputs: { ...villageInputs },
          fundFrom,
          sellHomeDetails,
          rentDetails,
          entryFeeFunding,
          entryFeeCgtOverrides,
          /**
           * WHO decided this funding, recorded at save. The entry contribution is always funded —
           * the tool pre-fills a default draw order because the projections cannot run otherwise —
           * so "the adviser allocated this" and "nobody touched the pre-fill" are materially
           * different advice positions that look identical in the saved figures. Recording it here
           * means the file itself answers the question later, rather than anyone reconstructing it
           * from memory. Carries into the SOA when the village module gains one.
           */
          entryFeeFundingProvenance: {
            source: isAdviserAllocatedEntryFee(entryFeeFunding) ? 'adviser_allocated' : 'tool_default_accepted',
            recordedAt: new Date().toISOString(),
            resolvedAllocation: { ...effectiveEntryFeeFunding },
          },
          rvOverallStrategy,
          rvInvestmentPropertyDetails,
          otherStrategies,
          annuityRecord: otherStrategies.annuity ? annuityRecord : null,
        },
        outputs: {
          summary,
          entryFee: summary.entryFee,
          annualOngoing: summary.annualOngoing,
          agePension: y0?.financials?.socialSecurity ?? 0,
          cashFlow: y0?.netCashflow ?? 0,
          legacyEstimate: legacyEstimateDisplay,
          // Read from the same `netWorth` field on the same rows `legacyEstimate` above is taken from,
          // so the year-by-year line and the final legacy figure can never disagree.
          netWorthSchedule: buildNetWorthSchedule(projections),
        },
        createdAt: new Date().toISOString(),
      },
    ]);
    setActiveSavedStrategyId(id);
    setStrategyName('');
  };

  const reopenStrategy = (strategy: SavedStrategy) => {
    const inputs = strategy.inputs || {};
    if (inputs.villageInputs) setVillageInputs(inputs.villageInputs as VillageInputs);
    setRvOverallStrategy((inputs.rvOverallStrategy as RvOverallStrategy) || { rentHome: false, downsizeHome: false, sellInvestmentProperty: false });
    setSellHomeDetails((inputs.sellHomeDetails as SellHomeDetails) || sellHomeDetails);
    setRentDetails((inputs.rentDetails as RentDetails) || rentDetails);
    setEntryFeeFunding((inputs.entryFeeFunding as FundingMap) || {});
    setEntryFeeCgtOverrides((inputs.entryFeeCgtOverrides as Record<string, number | null>) || {});
    setRvInvestmentPropertyDetails((inputs.rvInvestmentPropertyDetails as RvInvestmentPropertyDetails) || rvInvestmentPropertyDetails);
    const restoredOtherStrategies = inputs.otherStrategies as OtherStrategies | undefined;
    setOtherStrategies(
      restoredOtherStrategies || {
        gifting: false,
        giftingAmount: 0,
        giftingSource: 'bankAccounts',
        funeralBonds: false,
        funeralBondAmount: 0,
        funeralBondSource: 'bankAccounts',
        annuity: false,
      },
    );
    setAnnuityRecord((inputs.annuityRecord as AnnuityDetails) || null);
    if (restoredOtherStrategies?.annuity || inputs.annuityRecord) setShowOtherStrategies(true);
    setStrategyMode('manual');
    setShowStrategyOptions(true);
    setActiveSavedStrategyId(strategy.id);
  };

  const rvSavedStrategies = savedStrategies?.filter((s) => s.type === 'retirement_village') || [];
  const hasHome = !!(factFind?.home && n(factFind.home.value) > 0);
  // Matches the suggestion engine's own test for generating a sell-IP candidate
  // (generateRetirementVillageCandidates), so the narrative and the strategies can never disagree
  // about whether this client has an investment property to sell.
  const hasInvestmentProperty = (factFind?.assets?.investmentProperty?.length ?? 0) > 0;

  /**
   * One pool, two claimants. A dollar can pay the village entry fee or retire a debt, never both, so
   * each card's sliders are capped by what the OTHER has already claimed of that asset — the aged
   * care module's shared-pool rule (see DebtPayoffStrategy's header). Without this the debt card
   * offered an asset's FULL balance even after the entry fee had spent most of it.
   */
  const reservedForEntryFee = useMemo(
    () => Object.fromEntries(Object.entries(effectiveEntryFeeFunding || {}).map(([key, amount]) => [key, n(amount)])),
    [effectiveEntryFeeFunding],
  );
  const reservedForDebt = useMemo(
    () => Object.fromEntries(Object.entries(debtFunding || {}).map(([key, amount]) => [key, n(amount)])),
    [debtFunding],
  );
  const entryFeeFundingFromIp = useMemo(() => {
    const funding = effectiveEntryFeeFunding || {};
    return Object.keys(funding).some(id => id.startsWith('investmentProperty-') && n(funding[id]) > 0);
  }, [effectiveEntryFeeFunding]);

  const entryFeeAllocationBreakdown: { label: string; amount: number }[] = useMemo(() => {
    if (fundFrom !== 'liquid' || summary.entryFee <= 0 || !factFind?.assets) return [];
    const order = ['bankAccounts', 'termDeposits', 'accountBasedPensions', 'shares', 'investmentProperty'];
    const typeLabels: Record<string, string> = { bankAccounts: 'Bank accounts', termDeposits: 'Term deposits', accountBasedPensions: 'Account-based pensions', shares: 'Shares', investmentProperty: 'Investment property' };
    const out: { label: string; amount: number }[] = [];
    order.forEach(type => {
      const arr = factFind.assets![type];
      if (!arr) return;
      (arr || []).forEach(asset => {
        const assetId = `${type}-${asset.id}`;
        const amount = n(effectiveEntryFeeFunding?.[assetId]);
        if (amount <= 0) return;
        const label = asset.description || typeLabels[type] || type;
        out.push({ label, amount });
      });
    });
    return out;
  }, [fundFrom, summary.entryFee, factFind?.assets, effectiveEntryFeeFunding]);

  /*
   * GOAL LEADERS, with the aged care horizon rule (owner, 23 Sep 2026: "a flagged strategy should
   * not win a ranking"; 24 Sep: the nil-residual annuity led "best cashflow" here). A candidate
   * whose annuity needs more years than this client's age and health point to is set aside from
   * the leadership — never hidden: the disclosure below the cards names it and opens it.
   */
  const rvRecommended = useMemo<RecommendedStrategyEntry[]>(() => {
    if (!rvSuggestions) return [];
    const movers = ((rvSuggestions.strategies || []) as RvEvaluatedStrategy[]).filter((s) => s.id !== 'rv-stay-home' && !(s.inputs as Record<string, unknown> | undefined)?.stayHome);
    const eligible = movers.filter((s) => annuityHorizonWarningOf(s, factFind) == null);
    if (eligible.length === movers.length) return rvSuggestions.recommended;
    return buildRecommendedOptimalStrategies(eligible, (rvSuggestions.stayHome as EvaluatedStrategy | null | undefined) ?? null, { cashflowTarget: Number(rvSetup.cashflowTarget) || 0 });
  }, [rvSuggestions, factFind, rvSetup.cashflowTarget]);
  const rvGoalLeaders: RvEvaluatedStrategy[] = useMemo(() => mergeGoalLeaderStrategies(rvRecommended) as RvEvaluatedStrategy[], [rvRecommended]);
  /** Whether the cashflow-covered leader is also the best estate outright — said, not shown twice. */
  const coveredSameAsEstate = useMemo(() => coveredIsBestEstate(rvRecommended), [rvRecommended]);
  /** The cashflow-covered leader's case, for the line on its card. */
  const coveredNoteFor = (s: RvEvaluatedStrategy): string | null => {
    const v = rvRecommended.find((r) => r.label === CASHFLOW_COVERED_LABEL && r.strategy?.name === s.name)?.cashflowCovered;
    return v ? cashflowCoveredNote(v, Number(localLegacyYears) || 5, formatCurrency) : null;
  };
  /** The leaders the horizon rule set aside, so the screen can say so and open them. */
  const rvSetAside = useMemo(() => {
    if (!rvSuggestions) return [] as Array<{ strategy: RvEvaluatedStrategy; labels: string[]; warning: string }>;
    return (mergeGoalLeaderStrategies(rvSuggestions.recommended) as RvEvaluatedStrategy[])
      .map((s) => ({ strategy: s, labels: s.recommendationLabels || [], warning: annuityHorizonWarningOf(s, factFind) || '' }))
      .filter((x) => x.warning);
  }, [rvSuggestions, factFind]);
  /** Which goal's card is shown — aged care's chips (presentation only; the engine is not re-run). */
  const [optimisationFocus, setOptimisationFocus] = useState<'covered' | 'legacy' | 'cashflow' | 'pension' | 'all'>('legacy');
  /** How the full list is ordered — aged care's sort. */
  const [allSortBy, setAllSortBy] = useState<'legacy' | 'cashflow' | 'pension'>('legacy');
  const selectedStrategy: RvEvaluatedStrategy | null = useMemo(
    // Search the FULL evaluated set, not just the goal leaders — "Compare all strategies" lets the
    // user select any evaluated strategy, not only the three leaders.
    () => (strategyMode === 'optimised'
      ? ((((rvSuggestions?.strategies || []) as RvEvaluatedStrategy[]).find((s) => s.id === selectedSeId)) || rvGoalLeaders.find((s) => s.id === selectedSeId) || null)
      : null),
    [strategyMode, rvSuggestions, rvGoalLeaders, selectedSeId],
  );

  /**
   * Which strategy CARDS appear, and what each is badged as.
   *
   * Owner-caught (2026-08-26), two faults with one cause — the screen only ever rendered the goal
   * leaders:
   *   1. A run where nothing beat staying put on estate or Age Pension showed a single card. The
   *      withholding is honest (recommending something worse than doing nothing would not be), but a
   *      client who has already decided to move still needs to know which option is LEAST damaging.
   *      So a goal with no improver now shows its best-of-field, badged as not beating the baseline
   *      rather than presented as a recommendation.
   *   2. Selecting a strategy from "All modelled strategies" set the selection correctly and drove
   *      the panels — but if it was not a goal leader it had no card, so the screen looked unchanged
   *      and read as though the click had been ignored.
   */
  const seCards = useMemo(() => {
    if (!rvSuggestions) return [] as { strategy: RvEvaluatedStrategy; badges: string[]; belowBaseline?: string }[];
    const LABELS: Record<string, string> = { ...GOAL_CHIP_LABELS };
    const movers = ((rvSuggestions.strategies || []) as RvEvaluatedStrategy[]).filter((s) => !(s.inputs as Record<string, unknown> | undefined)?.stayHome);
    const out: { strategy: RvEvaluatedStrategy; badges: string[]; belowBaseline?: string }[] = [];
    const byId = new Map<string, { strategy: RvEvaluatedStrategy; badges: string[]; belowBaseline?: string }>();
    const push = (strategy: RvEvaluatedStrategy, badges: string[], belowBaseline?: string) => {
      const key = String(strategy.id ?? strategy.name ?? '');
      const existing = byId.get(key);
      if (existing) { badges.forEach((b) => { if (!existing.badges.includes(b)) existing.badges.push(b); }); return; }
      const entry = { strategy, badges: [...badges], belowBaseline };
      byId.set(key, entry);
      out.push(entry);
    };

    rvGoalLeaders.forEach((leader) => push(leader, (leader.recommendationLabels || []).map((l) => LABELS[l] || l)));

    const base = rvSuggestions.stayHome?.snapshot;
    const GOALS = [
      { label: 'Optimal Legacy', friendly: 'estate', pick: (s: RvEvaluatedStrategy) => s.snapshot?.legacyValue ?? 0, baseValue: base?.legacyValue ?? 0 },
      { label: 'Optimal Cashflow', friendly: 'cashflow', pick: (s: RvEvaluatedStrategy) => s.snapshot?.netCashflow ?? 0, baseValue: base?.netCashflow ?? 0 },
      { label: 'Optimal Age Pension', friendly: 'Age Pension', pick: (s: RvEvaluatedStrategy) => s.snapshot?.pension ?? 0, baseValue: base?.pension ?? 0 },
    ] as const;
    GOALS.forEach(({ label, friendly, pick, baseValue }) => {
      if (movers.length === 0) return;
      if (rvGoalLeaders.some((l) => (l.recommendationLabels || []).includes(label))) return;
      // Every option at nil pension is a finding, not a shortlist — the note below says so, and a
      // "best available $0" card would add nothing.
      if (label === 'Optimal Age Pension' && movers.every((s) => (s.snapshot?.pension ?? 0) <= 0)) return;
      const best = movers.reduce((top, s) => (pick(s) > pick(top) ? s : top), movers[0]!);
      push(best, [`Best available ${friendly}`], `Does not beat staying put (${formatCurrency(baseValue)}) — shown so you can see the least costly option for this goal.`);
    });

    if (selectedSeId && !byId.has(String(selectedSeId))) {
      const chosen = movers.find((s) => s.id === selectedSeId);
      if (chosen) push(chosen, ['Your selection']);
    }
    return out;
  }, [rvSuggestions, rvGoalLeaders, selectedSeId]);
  /*
   * `usingSe`: a Suggestion Engine strategy is selected, which drives its EXPLANATION (the recap
   * behind "Explain this") and its deep dive. It never drives the rail: in engine mode the plan
   * summary holds STAYING PUT while every card carries its own figures — the aged care pattern,
   * adopted here on 24 Sep 2026 (owner: "replicate ... the interplay with the Your Plan Summary").
   */
  const usingSe = strategyMode === 'optimised' && !!selectedStrategy;
  const [showAllSeStrategies, setShowAllSeStrategies] = useState(false);
  /** The cards the chosen goal shows; a chosen card that leads nothing shows under every goal. */
  const visibleSeCards = useMemo(() => {
    if (optimisationFocus === 'all') return seCards;
    // The same plan as Best estate is said in one line, not shown twice (owner, 24 Sep 2026).
    if (optimisationFocus === 'covered' && coveredSameAsEstate) return [];
    const badgesFor: Record<'covered' | 'legacy' | 'cashflow' | 'pension', string[]> = {
      covered: [CASHFLOW_COVERED_CHIP],
      legacy: [BEST_ESTATE_CHIP, 'Best available estate'],
      cashflow: ['Best cashflow', 'Best available cashflow'],
      pension: ['Best Age Pension', 'Best available Age Pension'],
    };
    return seCards.filter((c) => c.badges.some((b) => badgesFor[optimisationFocus].includes(b) || b === 'Your selection'));
  }, [seCards, optimisationFocus, coveredSameAsEstate]);
  /**
   * A real annuity quote, replacing the tool's indicative rate across the whole Suggestion Engine
   * field (owner, 2026-08-26). Null until the adviser enters one, in which case every candidate that
   * buys an annuity is re-modelled and the goals re-ranked on the quoted income — a provider's
   * actual figure can easily change which strategy wins, so it must re-rank rather than just relabel.
   */
  const [seAnnuityQuote, setSeAnnuityQuote] = useState<number | null>(null);
  const [seAnnuityQuoteDraft, setSeAnnuityQuoteDraft] = useState<number | ''>('');
  const effProjections: RvProjectionRow[] = usingSe ? (selectedStrategy!.projections || []) : projections;
  const effFundFrom = usingSe ? (selectedStrategy!.inputs?.fundFrom || fundFrom) : fundFrom;

  /*
   * HOW LONG THE MONEY LASTS — the section every other module's rail carries, and the one this
   * module needed most (2026-08-27). A village move commonly turns a small surplus into a deficit
   * of tens of thousands a year: the entry contribution leaves savings, the ongoing fees start, and
   * the Age Pension often goes with the home exemption. Reporting all three without ever saying how
   * many years the savings then last leaves out the question the client is actually asking.
   */
  /**
   * What the plan summary is describing, in the client's words — or null when it is describing
   * STAYING PUT. Manual Control only: the Suggestion Engine's cards each carry their own plan and
   * the rail holds the baseline beside them. Derived from the choices actually made, never a stored
   * label: a panel headed with a name that no longer matches the figures underneath is worse than
   * one with no name at all.
   *
   * PAYING FROM SAVINGS IS A CHOICE, AND IS NAMED (owner, 24 Sep 2026). The rail used to head "No
   * plan yet" over a plan the app had chosen — the entry contribution taken from savings in a
   * default order. In Manual Control that funding is the plan, so it is named as one.
   */
  const rvPlanSummaryName = useMemo<string | null>(() => {
    if (strategyMode !== 'manual') return null;
    const parts: string[] = [];
    if (rvOverallStrategy.downsizeHome) parts.push('Sell the family home');
    if (rvOverallStrategy.rentHome) parts.push('Rent the family home');
    if (rvOverallStrategy.sellInvestmentProperty) parts.push('Sell the investment property');
    if (!parts.length && summary.entryFee > 0) parts.push('Pay the entry contribution from savings');
    if (otherStrategies.annuity) parts.push('Commence an annuity');
    if (otherStrategies.gifting) parts.push('Gift to loved ones');
    if (otherStrategies.funeralBonds) parts.push('Buy a funeral bond');
    if (!parts.length) return null;
    const [lead, ...rest] = parts;
    return rest.length
      ? `${lead}, ${rest.map((p) => p.charAt(0).toLowerCase() + p.slice(1)).join(', ')}`
      : lead;
  }, [strategyMode, rvOverallStrategy, otherStrategies, summary.entryFee]);

  /** The lifetime annuity Manual Control buys, if any — said in the sentence and the recap when it returns nothing on death. */
  const manualAnnuity = useMemo<RvInBriefAnnuity | null>(() => {
    const ownerName = (o: unknown) => ((o === 'partner' ? factFind?.partner?.firstName : factFind?.client?.firstName) || 'you');
    if (fundFrom === 'sell_home' && n(sellHomeDetails.purchaseAnnuity) > 0) {
      return { amount: n(sellHomeDetails.purchaseAnnuity), residualPercent: n(sellHomeDetails.rvAnnuityResidualPercent), ownerName: ownerName(sellHomeDetails.rvAnnuityOwner) };
    }
    if (otherStrategies.annuity && annuityRecord && n(annuityRecord.purchaseAmount) > 0) {
      return { amount: n(annuityRecord.purchaseAmount), residualPercent: Math.round((Number(annuityRecord.residualCapital) || 0) * 100), ownerName: ownerName(annuityRecord.owner) };
    }
    return null;
  }, [fundFrom, sellHomeDetails, otherStrategies.annuity, annuityRecord, factFind]);
  /** The nil-residual annuity the recap must name — the selected card's, or Manual Control's. */
  const nilResidualAnnuityForRecap: RvInBriefAnnuity | null = (() => {
    const a = usingSe && selectedStrategy ? annuityOfStrategy(selectedStrategy, factFind) : manualAnnuity;
    return a && a.amount > 0 && Math.round(a.residualPercent) === 0 ? a : null;
  })();

  /**
   * The rail holds STAYING PUT — the one baseline — until Manual Control names a plan. In engine
   * mode it always does: each card carries its own figures, measured against this rail.
   */
  const railHoldsStayPut = !rvPlanSummaryName;
  const railProjections: RvProjectionRow[] = railHoldsStayPut ? stayPutProjections : projections;
  const rvRunway = useMemo(
    () => (railProjections.length ? planRunwayCopy(railProjections as never, Number(localLegacyYears) || 5) : null),
    [railProjections, localLegacyYears],
  );

  /**
   * The panel's "In brief": the SAME sentence template every engine card carries (rvInBrief.tsx),
   * fed here with Manual Control's own figures — the tiles beneath it — against staying put.
   */
  const rvInBrief = useMemo<React.ReactNode | null>(() => {
    if (!rvPlanSummaryName) return null;
    const cashBefore = initialFinancials?.cashFlow ?? 0;
    return (
      <RvInBrief
        entryFee={summary.entryFee}
        fundFrom={fundFrom}
        rentHome={rvOverallStrategy.rentHome}
        fromSaleAmount={fundFrom === 'liquid' ? null : propertyTowardEntry}
        fromSavingsAmount={fundFrom === 'liquid' ? null : liquidTargetForEntryFee}
        annuity={manualAnnuity}
        pensionBefore={initialFinancials?.socialSecurity ?? 0}
        pensionAfter={simulatedFinancials?.socialSecurity ?? 0}
        cashBefore={cashBefore}
        cashAfter={noVillageScenario ? cashBefore : (projections[0]?.netCashflow ?? cashBefore)}
        planLegacy={legacyEstimateDisplay}
        baseLegacy={manualBaselineLegacy}
        legacyYears={Number(localLegacyYears) || 0}
        shortfall={entryFeeShortfall}
      />
    );
  }, [rvPlanSummaryName, initialFinancials, simulatedFinancials, noVillageScenario, projections, legacyEstimateDisplay, manualBaselineLegacy, summary.entryFee, fundFrom, rvOverallStrategy.rentHome, localLegacyYears, entryFeeShortfall, propertyTowardEntry, liquidTargetForEntryFee, manualAnnuity]);
  const effRentHome = usingSe ? (effFundFrom === 'rent_home' || !!selectedStrategy!.inputs?.rentHome) : rvOverallStrategy.rentHome;
  const effDownsizeHome = usingSe ? (effFundFrom === 'sell_home') : rvOverallStrategy.downsizeHome;
  const effSellInvestmentProperty = usingSe ? (effFundFrom === 'investment_property') : rvOverallStrategy.sellInvestmentProperty;
  const effLegacyEstimate = usingSe
    ? (effProjections.length ? (effProjections[localLegacyYears - 1]?.netWorth ?? effProjections[effProjections.length - 1]?.netWorth ?? legacyEstimateDisplay) : legacyEstimateDisplay)
    : legacyEstimateDisplay;

  const rvInsightFinancials = useMemo(() => {
    const before = initialFinancials?.socialSecurity ?? 0;
    const after = noVillageScenario ? before : (effProjections[0]?.financials?.socialSecurity ?? before);
    const cashflowAfter = noVillageScenario ? (initialFinancials?.cashFlow ?? 0) : (effProjections[0]?.netCashflow ?? 0);
    let agePensionWhy = '';
    if (summary.entryFee > 0 && hasHome && (effFundFrom === 'liquid' || effFundFrom === 'rent_home' || effRentHome)) {
      // The entry contribution's treatment is decided by the Extra Allowable Amount test, and saying
      // it is "also assessed" unconditionally was wrong in the COMMON case: above the EAA the
      // resident is a homeowner and the contribution is exempt (verified against the engine — a
      // $500k entry on a kept $3.5m home assesses $3.5m, not $4.0m). Only at or below the EAA is the
      // contribution itself an assessable asset, and then the higher non-homeowner limits apply.
      agePensionWhy = summary.entryFee <= EXTRA_ALLOWABLE_AMOUNT
        ? `Your former principal home is assessable under the assets test (no 2‑year exemption in retirement village). Your entry contribution is at or below the extra allowable amount of ${formatCurrency(EXTRA_ALLOWABLE_AMOUNT)}, so it is also counted as an asset — but the higher non-homeowner thresholds apply, and Rent Assistance may be payable on your regular fees. The change in Age Pension reflects your total assessable assets and the income test.`
        : `Your former principal home is assessable under the assets test (no 2‑year exemption in retirement village). Your entry contribution is above the extra allowable amount of ${formatCurrency(EXTRA_ALLOWABLE_AMOUNT)}, so the contribution itself is not counted — it is treated as your home — but the lower homeowner thresholds then apply to everything else. The change in Age Pension reflects your total assessable assets and the income test.`;
    } else if (summary.entryFee > 0 && (effFundFrom === 'sell_home' || effFundFrom === 'investment_property')) {
      agePensionWhy = 'Your entry fee is funded from the sale of property. The change in Age Pension reflects your revised assessable assets and income (e.g. less home value, possible CGT, and how proceeds are invested).';
    } else if (summary.entryFee > 0) {
      agePensionWhy = 'The entry contribution is assessable under the assets test. Your Age Pension change reflects your total assessable assets and the income test.';
    }

    let cashflowDeficitNarrative = null;
    if (cashflowAfter < 0 && effProjections?.length > 0) {
      const y1 = effProjections[0];
      const drawdown = y1?.drawdownBreakdown || [];
      const firstSource = drawdown.length > 0 ? drawdown[0] : null;
      const firstLabel = firstSource?.label || '';
      const isBankOrTerm = firstLabel && !/account-based pension|abp|pension|share/i.test(firstLabel);

      if (isBankOrTerm) {
        let yearBankDepletes = null;
        let nextLabel = null;
        for (let i = 0; i < effProjections.length; i++) {
          const proj = effProjections[i];
          let totalBank = 0;
          (proj?.assets?.bankAccounts || []).forEach(acc => { totalBank += n(acc.value); });
          if (totalBank < 1000 && yearBankDepletes === null) {
            yearBankDepletes = proj!.year ?? (i + 1);
            const bd = proj!.drawdownBreakdown || [];
            for (const item of bd) {
              const norm = (item.label || '').toLowerCase();
              const amt = n(item.value);
              if (amt > 0 && (norm.includes('account-based pension') || norm.includes('abp') || norm.includes('pension'))) {
                nextLabel = item.label;
                break;
              }
              if (amt > 0 && norm.includes('share')) { nextLabel = item.label; break; }
              if (amt > 0 && (norm.includes('term') || norm.includes('deposit'))) { nextLabel = item.label; break; }
            }
            break;
          }
        }
        const bankOrFirstLabel = /bank|acc/i.test(firstLabel) && !/term deposit/i.test(firstLabel) ? 'bank account' : firstLabel;
        if (yearBankDepletes != null && nextLabel) {
          cashflowDeficitNarrative = `This deficit will be funded from your ${firstLabel}. Your ${bankOrFirstLabel} will be depleted by year ${yearBankDepletes}, at which stage the deficit will be funded from your ${nextLabel}.`;
        } else if (yearBankDepletes != null) {
          cashflowDeficitNarrative = `This deficit will be funded from your ${firstLabel}. Your ${bankOrFirstLabel} will be depleted by year ${yearBankDepletes}. After this point, the deficit will be funded from your remaining liquid assets (account-based pensions, then shares).`;
        } else {
          cashflowDeficitNarrative = `This deficit will be funded from your ${firstLabel} throughout the projection period.`;
        }
      } else if (firstSource) {
        cashflowDeficitNarrative = `This deficit will be funded from your ${firstLabel}.`;
      } else {
        const orderLabels: string[] = [];
        const types = ['bankAccounts', 'termDeposits', 'accountBasedPensions', 'shares'];
        const typeNames: Record<string, string> = { bankAccounts: 'bank accounts', termDeposits: 'term deposits', accountBasedPensions: 'account-based pensions', shares: 'shares' };
        types.forEach(t => {
          const arr = factFind?.assets?.[t];
          if (arr?.some(a => n(a.value) > 0)) orderLabels.push(typeNames[t] || t);
        });
        cashflowDeficitNarrative = orderLabels.length > 0
          ? `This deficit will be funded from your liquid assets in the following order: ${orderLabels.join(', then ')}.`
          : `This deficit will be funded from your liquid assets (bank accounts, then account-based pensions, then shares).`;
      }
    }

    const cashflowBefore = initialFinancials?.cashFlow ?? 0; // Dashboard cashflow, before any village move
    // State-aware estate-timing note: the exit entitlement is usually only paid when the unit re-sells, and
    // each state/territory sets a different statutory backstop for when the operator must pay regardless.
    const rvStateRules = getRetirementVillageStateRules(rvState);
    const estateTimingNote = rvStateRules.code
      ? `${rvStateRules.name}: ${rvStateRules.buyback.summary}`
      : 'Exit-entitlement timeframes differ by state and territory — select your state on the Retirement Village overview to see the rule that applies to you.';
    const rentAssistance = (() => {
      const bd = effProjections[0]?.financials?.cashFlowBreakdown || [];
      const item = bd.find((r) => /rent assistance/i.test(r.label || ''));
      return item ? n(item.value) : 0;
    })();
    return { agePensionBefore: before, agePensionAfter: after, agePensionWhy, cashflowBefore, cashflowAfter, villageOngoingAnnual: summary.annualOngoing ?? 0, legacyEstimate: effLegacyEstimate, noVillageScenario, cashflowDeficitNarrative, estateTimingNote, rentAssistance };
  }, [initialFinancials, effProjections, noVillageScenario, summary.entryFee, summary.annualOngoing, hasHome, effFundFrom, effRentHome, effLegacyEstimate, factFind?.assets, rvState]);

  /**
   * The plain-words recap, built once and read inside "Understanding your plan".
   *
   * The SAME component with the SAME props it had when it sat inline on the page — only its
   * position changed, matching the Aged Care benchmark. Inline, its three reasons were each
   * hidden behind a "(why?)" tap and the card still ran the width of the page; on its own
   * surface the reasons are simply visible, which is what a reader wanted from them.
   */
  const rvRecapNode = (
    <RvPlanRecap
    summary={summary}
    fin={rvInsightFinancials}
    fundFrom={effFundFrom as RvFundFrom}
    rentHome={effRentHome}
    hasHome={hasHome}
    yearsResidency={villageInputs.yearsResidency}
    seStrategyName={usingSe ? selectedStrategy?.name ?? null : null}
    firstName={factFind?.client?.firstName}
    entryFeeShortfall={usingSe ? 0 : entryFeeShortfall}
    canSellHome={hasHome}
    canSellInvestmentProperty={hasInvestmentProperty}
    allocations={usingSe
    ? ((selectedStrategy?.inputs as Record<string, unknown> | undefined)?.sellHomeDetails as Record<string, unknown> | undefined) ?? null
    : (effDownsizeHome ? (sellHomeDetails as unknown as Record<string, unknown>) : null)}
    netWorthToday={initialFinancials?.netWorth ?? 0}
    baselineLegacy={usingSe
    ? ((rvSuggestions?.stayHome as RvEvaluatedStrategy | undefined)?.snapshot?.legacyValue ?? null)
    : manualBaselineLegacy}
    legacyYears={localLegacyYears}
    partnerFirstName={factFind?.partner?.firstName}
    nilResidualAnnuity={nilResidualAnnuityForRecap}
    /* The departure fee accrued by the horizon on the contract as entered — with no exit modelled the
       contract is carried at what would come back, and this is what has come off it by then. */
    dmfAtHorizon={summary.entryFee > 0 ? exitAfter(villageInputs, villageInputs.yearsResidency > 0 ? Math.min(villageInputs.yearsResidency, localLegacyYears) : localLegacyYears).dmf : 0}
    homeValueToday={n(factFind?.home?.value)}
    homeGrowthPct={factFind?.home?.homeGrowthRate ?? DEFAULT_HOME_GROWTH_PCT}
    rentDetails={usingSe
    ? ((selectedStrategy?.inputs as Record<string, unknown> | undefined)?.rentDetails as { rentAmount?: number } | undefined) ?? null
    : (effRentHome ? rentDetails : null)}
    />
  );

  // Build suggestions when in Suggestion-Engine mode. We paint the loader first (60ms yield) THEN run the
  // (synchronous, multi-projection) evaluation, and hold the loader to a minimum so it reads as real work —
  // the same pattern that fixed the Aged Care engine flash.
  const entryFeeForSe = villageInputs.entryFee;

  /**
   * The just-in-time "how would new money be invested?" gate. The answer is an INPUT to the
   * modelling — invest strategies price at it — so it is asked BEFORE the engine runs, not
   * disclosed after a ranking that would reshuffle on the answer. Shown only when this run would
   * actually invest money, only until the client answers (answering saves to the profile, so it
   * is asked at most once, ever, across every module), and always skippable — a skip runs at
   * Conservative and leaves the question available next time, which is a different provenance
   * from choosing Conservative.
   */
  const [newMoneySkipped, setNewMoneySkipped] = useState(false);
  const seRunWouldInvest = useMemo(() => {
    if (!(entryFeeForSe > 0)) return false;
    try {
      return generateRetirementVillageCandidates(factFind, villageInputs).some((c) => {
        const d = c.sellHomeDetails;
        return !!d && ((Number(d.purchaseShares) || 0) > 0 || (Number(d.downsizerContribution) || 0) > 0 || (Number(d.partnerDownsizerContribution) || 0) > 0);
      });
    } catch { return false; }
  }, [factFind, villageInputs, entryFeeForSe]);
  const needsNewMoneyAnswer = strategyMode === 'optimised' && seRunWouldInvest && !hasAnsweredNewMoneyProfile(factFind) && !newMoneySkipped;
  const answerNewMoneyProfile = (id: InvestmentProfileId) => {
    if (setFactFind) {
      setFactFind((prev) => ({ ...prev, goals: { ...(prev.goals || {}), newMoneyInvestmentProfile: id } }));
    } else {
      // No persistence route (shouldn't happen from the app shell) — run rather than trap the user.
      setNewMoneySkipped(true);
    }
  };

  // The Transfer Balance Cap question, chained after the new-money one: only when this run would
  // COMMENCE a pension and a person's balances trip the $1.6m screen (transferBalanceCap.ts).
  // Skipping estimates cap usage from current pension balances — stated in the SOA assumptions —
  // and the engine splits any commencement at the cap either way.
  const [tbcSkipped, setTbcSkipped] = useState(false);
  const seRunCommencesPension = useMemo(() => {
    if (!(entryFeeForSe > 0)) return false;
    try {
      return generateRetirementVillageCandidates(factFind, villageInputs).some((c) => c.sellHomeDetails?.commenceIncomeStream === true);
    } catch { return false; }
  }, [factFind, villageInputs, entryFeeForSe]);
  const tbcPersonNeedingAnswer: 'client' | 'partner' | null = !tbcSkipped && seRunCommencesPension
    ? (tbcQuestionNeededFor(factFind as never, 'client') ? 'client'
      : (factFind?.adviceFor === 'couple' && tbcQuestionNeededFor(factFind as never, 'partner') ? 'partner' : null))
    : null;
  const needsTbcAnswer = strategyMode === 'optimised' && tbcPersonNeedingAnswer !== null;
  const answerTbc = (amount: number) => {
    if (setFactFind && tbcPersonNeedingAnswer) {
      const key = tbcPersonNeedingAnswer;
      setFactFind((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), transferBalanceUsed: amount } }));
    } else {
      setTbcSkipped(true);
    }
  };

  useEffect(() => {
    if (strategyMode !== 'optimised') return undefined;
    if (rvSetupOpen) { setSeLoading(false); return undefined; }
    if (needsNewMoneyAnswer || needsTbcAnswer) { setSeLoading(false); return undefined; }
    if (!(entryFeeForSe > 0)) { setRvSuggestions(null); setSeLoading(false); return undefined; }
    setSeLoading(true);
    const startedAt = Date.now();
    const t = setTimeout(async () => {
      let result: { strategies: EvaluatedStrategy[]; recommended: RecommendedStrategyEntry[] } | null = null;
      try { result = await buildRetirementVillageSuggestions(factFind, villageInputs, { legacyYears: localLegacyYears, villageEntryDate: effectiveVillageEntryDate || undefined, annuityIncomeOverride: seAnnuityQuote, setup: rvSetup }); } catch { result = null; }
      setRvSuggestions(result);
      const elapsed = Date.now() - startedAt;
      setTimeout(() => setSeLoading(false), Math.max(0, 2200 - elapsed));
    }, 60);
    return () => clearTimeout(t);
  }, [strategyMode, factFind, villageInputs, localLegacyYears, entryFeeForSe, needsNewMoneyAnswer, needsTbcAnswer, rvSetup, rvSetupOpen, seAnnuityQuote]);

  /**
   * "How does my plan compare?" for MANUAL control.
   *
   * The strongest protection against a client accepting a default and regretting it later is not a
   * warning, it is the alternative's number. Run on demand rather than automatically: the engine is
   * a real multi-projection evaluation, and a manual user who never asks should not pay for it on
   * every keystroke.
   */
  const [manualComparison, setManualComparison] = useState<{ loading: boolean; best: RvEvaluatedStrategy | null; ranAt: number } | null>(null);
  const runManualComparison = async () => {
    setManualComparison({ loading: true, best: null, ranAt: Date.now() });
    try {
      const result = await buildRetirementVillageSuggestions(factFind, villageInputs, { legacyYears: localLegacyYears, villageEntryDate: effectiveVillageEntryDate || undefined, setup: rvSetup });
      const movers = ((result?.strategies || []) as RvEvaluatedStrategy[]).filter((s) => s.id !== 'rv-stay-home');
      const best = movers.length
        ? movers.reduce((top, s) => ((s.snapshot?.legacyValue ?? 0) > (top.snapshot?.legacyValue ?? 0) ? s : top), movers[0]!)
        : null;
      setManualComparison({ loading: false, best, ranAt: Date.now() });
    } catch {
      setManualComparison({ loading: false, best: null, ranAt: Date.now() });
    }
  };
  // A changed plan invalidates a previous comparison — better to re-offer it than to show a stale one.
  useEffect(() => { setManualComparison(null); }, [villageInputs, entryFeeFunding, fundFrom, localLegacyYears]);

  // Nothing is selected for the reader (aged care's rule): a card is selected by choosing it. Cleared outside engine mode.
  useEffect(() => {
    if (strategyMode !== 'optimised' && selectedSeId !== null) setSelectedSeId(null);
  }, [strategyMode, selectedSeId]);

  /**
   * "Select Strategy" on an engine card SAVES it, exactly as it does in Aged Care — the saved list
   * is what the Dashboard and the advice document read, and a selection that lived only on this
   * screen was lost on the next click. Recorded with its source and what the engine recommended.
   */
  const selectEngineStrategy = (strategy: RvEvaluatedStrategy) => {
    setSelectedSeId(strategy.id ?? null);
    const id = `rv-se-${Date.now()}`;
    const recommended = rvGoalLeaders.map((l) => ({ labels: l.recommendationLabels || [], name: l.name }));
    setSavedStrategies((prev) => [
      ...(prev || []),
      {
        id,
        name: strategy.name || 'Suggested village strategy',
        type: 'retirement_village',
        source: 'suggestion_engine',
        inputs: {
          villageInputs: { ...villageInputs },
          ...((strategy.inputs || {}) as Record<string, unknown>),
          recommendedOptimalStrategies: recommended,
          clientSelectedIsAlternative: recommended.length > 0 && !recommended.some((r) => r.name === strategy.name),
        },
        outputs: {
          summary,
          entryFee: summary.entryFee,
          annualOngoing: summary.annualOngoing,
          agePension: strategy.snapshot?.pension ?? 0,
          cashFlow: strategy.snapshot?.netCashflow ?? 0,
          legacyEstimate: strategy.snapshot?.legacyValue ?? 0,
          netWorthSchedule: buildNetWorthSchedule(strategy.projections || []),
        },
        createdAt: new Date().toISOString(),
      } as SavedStrategy,
    ]);
    setActiveSavedStrategyId(id);
  };


  return (
    <div className="max-w-[1520px] mx-auto">
      <div className="flex items-center gap-4 mb-4">
        <button onClick={onBack} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 font-medium"><ChevronLeft size={20} /> Back</button>
        <button onClick={() => setActivePage('Dashboard')} className="text-blue-600 hover:text-blue-800 font-medium">Back to Dashboard</button>
      </div>
      <h1 className="text-4xl font-bold text-gray-900 mb-2"><GradientText>Retirement Village Funding Analyser</GradientText></h1>
      <p className="text-lg text-slate-700 leading-relaxed">
        Three short steps: what the village costs, where {factFind?.client?.firstName || 'you'} {factFind?.adviceFor === 'couple' && factFind?.partner?.firstName ? `and ${factFind.partner.firstName} stand` : (factFind?.client?.firstName ? 'stands' : 'stand')} today, and the ways to fund the move.
      </p>
      <AnalyserSteps
        step={step}
        onStep={goToStep}
        steps={[
          { title: 'What the village costs', sub: 'The contract and the move-in date' },
          { title: `Where ${factFind?.client?.firstName || 'you'} ${factFind?.client?.firstName ? 'stands' : 'stand'} today`, sub: 'Four numbers, staying put' },
          { title: 'Ways to fund the move', sub: 'Recommended plans, or build your own' },
        ]}
      />

      {step === 1 && (
        <div className="mt-8 space-y-6">
          <Card>
            <h3 className="text-xl font-bold text-gray-800 mb-4">Village & accommodation costs</h3>
            <p className="text-sm text-gray-600 mb-3">Many villages provide a <strong>disclosure statement</strong> that sets out these figures—if you have or expect one, use it to fill in the estimates below.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2 text-sm">
                <label className="flex justify-between items-center gap-2"><span className="flex items-center gap-1">Entry payment ($) <WhatsThis text={RV_COST_TOOLTIPS.entryFee} /></span><CurrencyInput value={villageInputs.entryFee ?? ''} onChange={v => setVillageInputs(prev => ({ ...prev, entryFee: v ?? 0 }))} className="w-28" /></label>
                {/* One row for the fee and how often it is charged (owner, 24 Sep 2026: a separate
                    frequency row asked the same question twice). */}
                <label className="flex justify-between items-center gap-2"><span className="flex items-center gap-1">Regular fees ($) <WhatsThis text={RV_COST_TOOLTIPS.ongoingFees} /></span>
                  <span className="flex items-center gap-1.5">
                    <CurrencyInput value={villageInputs.ongoingWeeklyOrMonthly ?? ''} onChange={v => setVillageInputs(prev => ({ ...prev, ongoingWeeklyOrMonthly: v ?? 0 }))} className="w-28" />
                    <select value={villageInputs.ongoingFrequency} onChange={e => setVillageInputs(prev => ({ ...prev, ongoingFrequency: e.target.value as VillageInputs['ongoingFrequency'] }))} className="border rounded px-2 py-1" aria-label="How often the regular fees are charged"><option value="monthly">a month</option><option value="weekly">a week</option></select>
                  </span>
                </label>
                <label className="flex justify-between items-center gap-2"><span className="flex items-center gap-1">Departure fee (%/yr) <WhatsThis text={RV_COST_TOOLTIPS.dmfPctPerYear} /></span><input type="number" value={villageInputs.dmfPctPerYear} onChange={e => setVillageInputs(prev => ({ ...prev, dmfPctPerYear: parseFloat(e.target.value) || 0 }))} className="w-28 border rounded px-2 py-1" /></label>
                <label className="flex justify-between items-center gap-2"><span className="flex items-center gap-1">Departure fee cap (years) <WhatsThis text={RV_COST_TOOLTIPS.dmfCapYears} /></span><input type="number" value={villageInputs.dmfCapYears} onChange={e => setVillageInputs(prev => ({ ...prev, dmfCapYears: parseFloat(e.target.value) || 0 }))} className="w-28 border rounded px-2 py-1" /></label>
              </div>
              <div className="space-y-2 text-sm">
                {/* "Years in village" did two jobs — the departure fee, and an exit the projection
                    modelled with the client then living nowhere (owner, 24 Sep 2026). Staying for
                    life is the default; the fee after 3, 5 and 10 years is shown beside the costs. */}
                <label className="flex justify-between items-center gap-2"><span className="flex items-center gap-1">How long will you stay? <WhatsThis text={RV_COST_TOOLTIPS.yearsResidency} /></span>
                  <select
                    value={villageInputs.yearsResidency > 0 ? 'years' : 'life'}
                    onChange={e => setVillageInputs(prev => ({ ...prev, yearsResidency: e.target.value === 'life' ? 0 : Math.max(1, prev.yearsResidency || 10) }))}
                    className="border rounded px-2 py-1"
                    aria-label="How long will you stay in the village?"
                  >
                    <option value="life">For the rest of my life</option>
                    <option value="years">A set number of years</option>
                  </select>
                </label>
                {villageInputs.yearsResidency > 0 && (
                  <label className="flex justify-between items-center gap-2"><span className="flex items-center gap-1 text-slate-600">Years before leaving</span><input type="number" min={1} value={villageInputs.yearsResidency} onChange={e => setVillageInputs(prev => ({ ...prev, yearsResidency: Math.max(1, parseFloat(e.target.value) || 1) }))} className="w-28 border rounded px-2 py-1" aria-label="Years before leaving the village" /></label>
                )}
                <label className="flex justify-between items-center gap-2"><span className="flex items-center gap-1">Unit value growth (%/yr) <WhatsThis text={RV_COST_TOOLTIPS.growthRatePct} /></span><input type="number" value={villageInputs.growthRatePct} onChange={e => setVillageInputs(prev => ({ ...prev, growthRatePct: parseFloat(e.target.value) || 0 }))} className="w-28 border rounded px-2 py-1" /></label>
                <label className="flex justify-between items-center gap-2"><span className="flex items-center gap-1">Your share of value increase (0–100%) <WhatsThis text={RV_COST_TOOLTIPS.capitalGainShare} /></span><input type="number" step="0.1" min="0" max="100" value={villageInputs.capitalGainShareToResident * 100} onChange={e => setVillageInputs(prev => ({ ...prev, capitalGainShareToResident: (parseFloat(e.target.value) || 0) / 100 }))} className="w-28 border rounded px-2 py-1" /></label>
                <label className="flex justify-between items-center gap-2"><span className="flex items-center gap-1">Reinstatement cap ($) <WhatsThis text={RV_COST_TOOLTIPS.reinstatementCap} /></span><CurrencyInput value={villageInputs.reinstatementCap ?? ''} onChange={v => setVillageInputs(prev => ({ ...prev, reinstatementCap: v ?? 0 }))} className="w-28" /></label>
              </div>
            </div>
            <div className="mt-4 flex flex-col md:flex-row md:items-end gap-4 p-3 rounded-lg border border-slate-200 bg-slate-50/60">
              <div className="flex-1">
                <label htmlFor="rv-entry-date" className="block text-sm font-semibold text-gray-800">When are you planning to move in?</label>
                <p className="text-xs text-gray-600 mt-1">
                  Leave this blank to model the move from today — the right choice if you&apos;re still
                  exploring. Choose a future date and we&apos;ll show life at home as it is until then,
                  with the move, the entry payment and the village fees all starting on the day you pick.
                </p>
              </div>
              <div>
                <input
                  id="rv-entry-date"
                  type="date"
                  value={villageEntryDate}
                  onChange={(e) => setVillageEntryDate(e.target.value)}
                  className="px-4 py-2 bg-white border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                />
                {villageEntryDateInPast && (
                  <p className="text-xs text-amber-700 mt-1">A past date models as &ldquo;moving in now&rdquo;.</p>
                )}
              </div>
            </div>
          </Card>
          <div className="flex flex-wrap items-center justify-end gap-3">
            <button
              type="button"
              onClick={() => goToStep(2)}
              disabled={!(villageInputs.entryFee > 0)}
              className="inline-flex h-11 items-center gap-2 rounded-lg bg-gradient-to-r from-blue-600 to-teal-500 px-5 text-[14px] font-semibold text-white shadow-sm hover:from-blue-700 hover:to-teal-600 disabled:cursor-not-allowed disabled:opacity-50"
            >
              See where {factFind?.client?.firstName || 'you'} {factFind?.client?.firstName ? 'stands' : 'stand'} today <ArrowRight size={16} />
            </button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="mt-8">
          <RvStayPutHero
            factFind={factFind}
            todayFinancials={initialFinancials}
            stayPutLegacy={manualBaselineLegacy}
            runway={stayPutProjections.length ? planRunwayCopy(stayPutProjections as never, Number(localLegacyYears) || 5) : null}
            legacyYears={localLegacyYears}
            villageInputs={villageInputs}
            annualOngoing={summary.annualOngoing}
            onLegacyYearsChange={setLocalLegacyYears}
            onBack={() => goToStep(1)}
            onNext={() => goToStep(3)}
            onAssumptions={() => setActivePage('AssumptionsPage')}
            onShowProjections={() => { setProjectionsSource('stayPut'); setShowProjectionsModal(true); }}
          />
        </div>
      )}

      {step === 3 && (
      <div className="mt-8 grid grid-cols-1 xl:grid-cols-5 gap-8 items-start">
        <div className="xl:col-span-3 space-y-6">
          <Card>
            <ModeChooser
              value={strategyMode}
              onSelect={(id) => {
                if (id === 'manual') { setStrategyMode('manual'); setShowStrategyOptions(true); }
                else { setStrategyMode('optimised'); setShowStrategyOptions(false); if (!rvSetupSeen) setRvSetupOpen(true); }
              }}
              options={[
                {
                  id: 'manual',
                  title: 'Manual Control',
                  blurb: 'Enter the village contract yourself — entry price, ongoing fees, the exit fee — and choose how the entry contribution is funded: savings, renting the home out, or selling.',
                  chip: 'Advisers & confident users',
                },
                {
                  id: 'optimised',
                  title: 'Suggestion Engine',
                  blurb: 'It compares the funding paths for this village — and staying put — then shows the strongest for each goal.',
                  chip: 'Most people start here',
                },
              ]}
            />
          </Card>
          <RvAnalysisSetupModal
            isOpen={rvSetupOpen}
            onClose={() => { setRvSetupOpen(false); setRvSetupSeen(true); }}
            factFind={factFind}
            initial={rvSetup}
            netFreedAfterEntry={rvNetFreedAfterEntry}
            askNewMoney={seRunWouldInvest && !hasAnsweredNewMoneyProfile(factFind)}
            tbcPerson={tbcPersonNeedingAnswer}
            onContinue={(next, extras) => {
              setRvSetup(next);
              setRvSetupSeen(true);
              setRvSetupOpen(false);
              if (extras.newMoneyProfile) answerNewMoneyProfile(extras.newMoneyProfile); else setNewMoneySkipped(true);
              if (tbcPersonNeedingAnswer) { if (extras.tbcAmount != null) answerTbc(extras.tbcAmount); else setTbcSkipped(true); }
              if (setFactFind) {
                setFactFind((prev) => ({
                  ...prev,
                  goals: {
                    ...(prev.goals || {}),
                    rvPurchaseState: next.purchaseState || '',
                    rvOldUnitPaysAtMove: next.oldUnitPaysAtMove !== false,
                    rvAnnuityShareCapPct: Number(next.annuityShareCapPct),
                    // The general spelling every module reads, and the village's original one.
                    annuityAppetite: next.annuityAppetite ?? 'unsure',
                    annuityResidual: next.annuityResidual ?? 'both',
                    rvAnnuityAppetite: next.annuityAppetite ?? 'unsure',
                    rvAnnuityResidual: next.annuityResidual ?? 'both',
                    rvCashflowTarget: Number(next.cashflowTarget) || 0,
                  },
                }));
              }
            }}
          />

          {strategyMode === 'manual' && showStrategyOptions && (
            <>
              <Card>
                <h3 className="text-xl font-bold text-gray-800 mb-4">Property strategy options</h3>
                {hasHome || ((factFind?.assets?.investmentProperty?.length ?? 0) > 0) ? (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {hasHome && (
                      <>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input type="checkbox" checked={rvOverallStrategy.rentHome} onChange={e => setRvOverallStrategy(s => ({ ...s, rentHome: e.target.checked, downsizeHome: e.target.checked ? false : s.downsizeHome }))} className="rounded" />
                          <span>Rent the family home</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input type="checkbox" checked={rvOverallStrategy.downsizeHome} onChange={e => setRvOverallStrategy(s => ({ ...s, downsizeHome: e.target.checked, rentHome: e.target.checked ? false : s.rentHome, sellInvestmentProperty: e.target.checked ? false : s.sellInvestmentProperty }))} className="rounded" />
                          <span>Sell/downsize the family home</span>
                        </label>
                      </>
                    )}
                    {(factFind?.assets?.investmentProperty?.length ?? 0) > 0 && (
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={rvOverallStrategy.sellInvestmentProperty} onChange={e => {
                          const on = e.target.checked;
                          setRvOverallStrategy(s => ({ ...s, sellInvestmentProperty: on, downsizeHome: on ? false : s.downsizeHome }));
                          // Selling the property to fund the move means the sale goes toward the entry
                          // contribution: start the slider there rather than at "the sale covers $0".
                          if (on) setRvInvestmentPropertyDetails(prev => (n(prev.entryFeeAllocation) > 0 ? prev : { ...prev, entryFeeAllocation: villageInputs.entryFee }));
                        }} className="rounded" />
                        <span>Sell investment property</span>
                      </label>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-slate-700 bg-white border border-slate-200 border-l-4 border-l-amber-400 p-3 rounded-lg">Property strategies are only available if you have a family home or an investment property. You can fund the entry fee from liquid assets below.</p>
                )}
                {rvOverallStrategy.rentHome && (
                  <div className="mt-4 p-4 bg-gray-50 rounded-lg border space-y-3">
                    <h4 className="font-semibold">Rental assumptions</h4>
                    <p className="text-xs text-gray-600">Values below are prepopulated from your Client Profile (fact find) if entered there; you can change them here.</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div><CurrencyInput label="Weekly rent ($)" value={rentDetails.rentAmount ?? ''} onChange={v => setRentDetails(prev => ({ ...prev, rentAmount: v ?? 0 }))} className="" /></div>
                      <div><CurrencyInput label="Annual rent expenses ($)" value={rentDetails.rentExpenses ?? ''} onChange={v => setRentDetails(prev => ({ ...prev, rentExpenses: v ?? 0 }))} className="" /></div>
                    </div>
                    {/* The tax consequence of renting the former home, said where renting is set up.
                        The pension side is covered by "What the entry payment does" above; this is
                        the part that only bites years later, when the house is eventually sold. */}
                    <RentedFormerHomeCgtNote homeValue={n(factFind?.home?.value)} />
                  </div>
                )}
                {rvOverallStrategy.downsizeHome && hasHome && (
                  <RVSellHomeAllocator factFind={factFind} setFactFind={setFactFind} details={sellHomeDetails} setDetails={setSellHomeDetails} entryFee={villageInputs.entryFee} />
                )}
                {/* Full width, under the choices — inside the grid it rendered as one narrow column
                    (owner, 24 Sep 2026: "the CGT box is still not showing correctly"). */}
                {rvOverallStrategy.sellInvestmentProperty && (
                  <div className="mt-4"><InvestmentPropertyEntryFeeNote /></div>
                )}
                {rvOverallStrategy.sellInvestmentProperty && (factFind?.assets?.investmentProperty?.length ?? 0) > 0 && (
                  <RVInvestmentPropertyAllocator factFind={factFind} entryFee={villageInputs.entryFee} details={rvInvestmentPropertyDetails} setDetails={setRvInvestmentPropertyDetails} />
                )}
              </Card>
              {(fundFrom === 'liquid' || liquidTargetForEntryFee > 0) && (
                <Card>
                  {fundFrom !== 'liquid' && (
                    <p className="mb-4 rounded-lg border border-slate-200/70 border-l-4 border-l-blue-500 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm">
                      The sale covers <span className="font-semibold text-slate-900">{formatCurrency(propertyTowardEntry)}</span> of the{' '}
                      <span className="font-semibold text-slate-900">{formatCurrency(villageInputs.entryFee)}</span> entry contribution. The remaining{' '}
                      <span className="font-semibold text-slate-900">{formatCurrency(liquidTargetForEntryFee)}</span> comes from savings — allocate it below.
                    </p>
                  )}
                  <RVEntryFeeFunding
                    factFind={factFind}
                    entryFee={liquidTargetForEntryFee}
                    entryFeeFunding={entryFeeFunding}
                    setEntryFeeFunding={setEntryFeeFunding}
                    defaultAllocation={defaultEntryFeeAllocation}
                    entryFeeCgtOverrides={entryFeeCgtOverrides}
                    setEntryFeeCgtOverrides={setEntryFeeCgtOverrides}
                    reservedByAsset={reservedForDebt}
                  />
                </Card>
              )}

              {/* The alternative's NUMBER, on demand. A client who has seen what the strongest
                  modelled option projects and chosen their own plan anyway has made a decision; one
                  who never saw it has only accepted a default. Manual mode only — the Suggestion
                  Engine already shows the whole field. */}
              {strategyMode === 'manual' && summary.entryFee > 0 && (
                <Card>
                  <h3 className="text-xl font-bold text-gray-800 mb-1">How does this plan compare?</h3>
                  {!manualComparison ? (
                    <>
                      <p className="text-sm text-gray-600 mb-3">Model every funding approach we can build for this village and see how your plan measures up. Nothing changes on this screen — it&apos;s a comparison, not a switch.</p>
                      <Button variant="secondary" size="sm" onClick={() => { void runManualComparison(); }}>Compare with the strongest option</Button>
                    </>
                  ) : manualComparison.loading ? (
                    <p className="text-sm text-gray-600">Modelling the alternatives…</p>
                  ) : manualComparison.best ? (() => {
                    const bestLegacy = manualComparison.best.snapshot?.legacyValue ?? 0;
                    const yourLegacy = legacyEstimateDisplay ?? 0;
                    const gap = Math.round(bestLegacy - yourLegacy);
                    return (
                      <>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                            <p className="text-[11px] uppercase tracking-wide text-slate-500">Your plan — projected estate</p>
                            <p className="text-lg font-bold text-gray-900">{formatCurrency(yourLegacy)}</p>
                          </div>
                          <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
                            <p className="text-[11px] uppercase tracking-wide text-blue-700">Strongest modelled option</p>
                            <p className="text-lg font-bold text-blue-900">{formatCurrency(bestLegacy)}</p>
                            <p className="text-xs text-blue-800 mt-0.5">{manualComparison.best.name}</p>
                          </div>
                        </div>
                        {gap > 1000 ? (
                          <p className="text-sm text-gray-700">
                            The strongest option we modelled projects <strong>{formatCurrency(gap)}</strong> more estate than your current plan
                            over {localLegacyYears} years. That doesn&apos;t make it right for you — it may involve selling assets or accepting risks
                            you&apos;d rather not — but it&apos;s worth understanding before you settle. Switch to the <strong>Suggestion Engine</strong> to see how it works.
                          </p>
                        ) : gap < -1000 ? (
                          <p className="text-sm text-emerald-800">Your plan projects <strong>{formatCurrency(Math.abs(gap))}</strong> more estate than anything the engine built. Worth saving.</p>
                        ) : (
                          <p className="text-sm text-emerald-800">Your plan is within {formatCurrency(Math.abs(gap))} of the strongest option we modelled — essentially the same outcome.</p>
                        )}
                      </>
                    );
                  })() : (
                    <p className="text-sm text-gray-600">We couldn&apos;t model alternatives for this scenario. Your plan stands on the numbers shown on this page.</p>
                  )}
                </Card>
              )}

              {/* With the funding decisions, not after Save Strategy: money spent clearing a debt is
                  money that cannot pay the entry fee, so the card belongs beside the allocator it
                  competes with — the same read-order-follows-money-order rule the Aged Care page uses. */}
              <DebtPayoffStrategy
                factFind={factFind as never}
                debtPayments={debtPayments}
                setDebtPayments={setDebtPayments}
                debtFunding={debtFunding}
                setDebtFunding={setDebtFunding}
                repaymentBasis={debtRepaymentBasis}
                setRepaymentBasis={setDebtRepaymentBasis}
                competingUseNote="The money comes from the same assets that can fund the village entry fee, so what you spend here reduces what is left for the entry payment."
                reservedByAsset={reservedForEntryFee}
              />

              <RVOtherStrategiesSection
                factFind={factFind}
                liquidFactFindAfterVillage={liquidFactFindAfterVillage}
                entryFeeAllocations={fundFrom === 'liquid' ? effectiveEntryFeeFunding : {}}
                otherStrategies={otherStrategies}
                setOtherStrategies={setOtherStrategies}
                annuityRecord={annuityRecord}
                setAnnuityRecord={setAnnuityRecord}
                showOtherStrategies={showOtherStrategies}
                setShowOtherStrategies={setShowOtherStrategies}
              />

              {/* The interpretation layer — same pattern as the aged care module. Quotes only
                  what the page already computed for its panels (village summary +
                  rvInsightFinancials, for whichever view is live), so it cannot disagree with
                  the Financial Impact panel or the insight panel. */}

              <Card>
                <h3 className="text-xl font-bold text-gray-800 mb-4">Save Strategy</h3>
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <input
                      type="text"
                      placeholder="Enter strategy name (e.g., Conservative Approach)"
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      id="rvStrategyNameInput"
                      value={strategyName}
                      onChange={(e) => setStrategyName(e.target.value)}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const el = document.getElementById('rvStrategyNameInput') as HTMLInputElement | null;
                        saveStrategy(el?.value || strategyName);
                        if (el) el.value = '';
                      }}
                      className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold"
                    >
                      Save Strategy
                    </button>
                  </div>
                  {rvSavedStrategies.length > 0 && (
                    <div className="space-y-3">
                      <div className="p-3 bg-blue-50 border-l-4 border-blue-400 rounded-r-lg">
                        <p className="text-sm text-blue-700 mb-2">
                          <strong>{rvSavedStrategies.length}</strong> retirement village{' '}
                          {rvSavedStrategies.length === 1 ? 'strategy' : 'strategies'} saved.
                        </p>
                        <div className="space-y-1">
                          {rvSavedStrategies.map((strategy) => (
                            <div
                              key={strategy.id}
                              className={`flex items-center justify-between text-xs rounded-lg px-2 py-1.5 transition-colors ${
                                activeSavedStrategyId === strategy.id
                                  ? 'bg-white ring-2 ring-blue-500 border border-blue-500 shadow-sm'
                                  : 'text-gray-600'
                              }`}
                            >
                              <span className="text-gray-600">{strategy.name}</span>
                              <button
                                type="button"
                                onClick={() => reopenStrategy(strategy)}
                                className="text-blue-600 hover:text-blue-800 underline font-semibold"
                              >
                                Reopen
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                      {rvSavedStrategies.length > 1 && (
                        <button
                          type="button"
                          onClick={() => setShowCompareModal(true)}
                          className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-semibold"
                        >
                          Compare Strategies ({rvSavedStrategies.length})
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </Card>
            </>
          )}

          {strategyMode === 'optimised' && (
            <Card>
              <div className="flex items-center gap-3 mb-1">
                <IconTile icon={TrendingUpIcon} tone="brand" variant="soft" size="sm" className="hidden sm:inline-flex" />
                <h3 className="text-xl font-bold text-gray-800">Suggested strategies</h3>
                <button
                  type="button"
                  onClick={() => setRvSetupOpen(true)}
                  className="ml-auto text-sm font-medium text-blue-700 hover:text-blue-900 hover:underline whitespace-nowrap"
                >
                  Adjust setup
                </button>
              </div>
              {entryFeeForSe <= 0 ? (
                <p className="text-sm text-slate-700 bg-white border border-slate-200 border-l-4 border-l-amber-400 rounded-r-lg p-3 mt-3">
                  Enter your <strong>entry payment</strong> and <strong>ongoing fees</strong> in the village costs panel above, and I&apos;ll compare the best ways to fund and structure your move.
                </p>
              ) : needsNewMoneyAnswer ? (
                <NewMoneyProfileGate onAnswer={answerNewMoneyProfile} onSkip={() => setNewMoneySkipped(true)} />
              ) : needsTbcAnswer ? (
                <TransferCapGate
                  name={(tbcPersonNeedingAnswer === 'partner' ? factFind?.partner?.firstName : factFind?.client?.firstName) || 'this person'}
                  onAnswer={answerTbc}
                  onSkip={() => setTbcSkipped(true)}
                />
              ) : seLoading ? (
                <SuggestionEngineAnalysisLoader
                  title="Optimising your village strategies"
                  subtitle="We're modelling several ways to fund and structure your move — this only takes a moment."
                />
              ) : rvGoalLeaders.length === 0 ? (
                <p className="text-sm text-gray-600 mt-3">We couldn&apos;t generate suggestions for this scenario. Use <strong>Manual Control</strong> to model your options.</p>
              ) : (
                <>
                  <p className="text-sm text-gray-600 mt-1 mb-2">I&apos;ve modelled <strong>{rvSuggestions?.strategies?.length ?? 0}</strong> ways to fund and structure your move and picked the strongest for each goal below. Each card carries its own figures, measured against <strong>staying put</strong> — the position on the right. <strong>Explain this</strong> sets a plan out in plain words, with the year-by-year detail behind it; <strong>What could go wrong</strong> is the honest list of its risks.</p>
                  <div className="mb-4">
                    <button type="button" onClick={() => setShowAllSeStrategies(true)} className="text-sm font-semibold text-blue-600 hover:text-blue-800 hover:underline">
                      Compare all {rvSuggestions?.strategies?.length ?? 0} strategies →
                    </button>
                  </div>
                  {/* Goal-first, as aged care: which goal's card to show (presentation only). */}
                  <div className="mb-4 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                    <p className="text-sm font-semibold text-slate-800 mb-3">Show strategies optimised for</p>
                    <div className="flex flex-wrap gap-2">
                      {([
                        { id: 'legacy', label: BEST_ESTATE_CHIP },
                        { id: 'covered', label: CASHFLOW_COVERED_CHIP },
                        { id: 'cashflow', label: 'Best cashflow' },
                        { id: 'pension', label: 'Best Age Pension' },
                      ] as const).map(({ id, label }) => (
                        <button
                          key={id}
                          type="button"
                          onClick={() => setOptimisationFocus(id)}
                          className={`flex-1 min-w-[140px] rounded-lg px-4 py-2.5 text-sm font-semibold transition-all border-2 ${
                            optimisationFocus === id
                              ? 'border-blue-600 bg-blue-50 text-blue-900 shadow-sm'
                              : 'border-slate-200 bg-slate-50 text-slate-700 hover:border-slate-300 hover:bg-white'
                          }`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                    <p className="text-xs text-slate-600 mt-3">
                      {optimisationFocus === 'covered' && `The best estate among the plans that still pay the bills after the village fees${Number(rvSetup.cashflowTarget) > 0 ? ` and keep ${formatCurrency(Number(rvSetup.cashflowTarget))} a year to spare` : ''} — or, where none does, whose shortfall the savings carry for the whole period. The card says which.`}
                      {optimisationFocus === 'legacy' && 'The strategy that leaves the most to the family after the projection period, against staying put.'}
                      {optimisationFocus === 'cashflow' && 'The strongest yearly cashflow from this run.'}
                      {optimisationFocus === 'pension' && 'The highest modelled Age Pension, where any strategy produces one.'}
                    </p>
                  </div>
                  {optimisationFocus === 'covered' && coveredSameAsEstate && (
                    <div className="mb-4"><SameAsBestEstateNote onShowBestEstate={() => setOptimisationFocus('legacy')} /></div>
                  )}
                  {visibleSeCards.length === 0 && !(optimisationFocus === 'covered' && coveredSameAsEstate) && (
                    <p className="mb-4 rounded-lg border border-slate-200/70 border-l-4 border-l-amber-500 bg-white px-3 py-2 text-sm text-slate-700">
                      {optimisationFocus === 'pension' && ((rvSuggestions?.strategies || []) as RvEvaluatedStrategy[]).every((s) => (s.snapshot?.pension ?? 0) <= 0)
                        ? 'No option modelled produces any Age Pension — the assessable assets stay above the cut-off in every one, so there is no pension to improve. That is a finding, not a gap.'
                        : 'No option improved on staying put, so nothing is recommended for this goal.'}
                    </p>
                  )}
                  <div className="space-y-4">
                    {visibleSeCards.map(({ strategy: leader, badges, belowBaseline }) => (
                      <RvStrategyCard
                        key={leader.id || leader.name}
                        strategy={leader}
                        factFind={factFind}
                        badges={badges}
                        belowBaseline={belowBaseline}
                        baseline={(rvSuggestions?.stayHome as RvEvaluatedStrategy | null | undefined) ?? null}
                        entryFee={summary.entryFee}
                        legacyYears={localLegacyYears}
                        clientName={factFind?.client?.firstName || 'you'}
                        partnerName={factFind?.partner?.firstName || 'your partner'}
                        isSelected={leader.id === selectedSeId}
                        goalNote={coveredNoteFor(leader)}
                        onSelect={() => selectEngineStrategy(leader)}
                        onExplain={() => { setSelectedSeId(leader.id ?? null); setShowRvExplanation(true); }}
                        onRisks={() => setRiskStrategy(leader)}
                        onGenerateSoa={() => generateRvSoa(leader)}
                        alternative={alternativeAssetNote(
                          leader,
                          (rvSuggestions?.strategies || []) as RvEvaluatedStrategy[],
                          (s) => (s.snapshot as { legacyValue?: number } | undefined)?.legacyValue ?? null,
                          Number(localLegacyYears) || 5,
                        )}
                        annuityBlock={(leader.annuityIncome ?? 0) > 0 ? (
                          <div className="mt-3 rounded-lg border border-slate-200 border-l-4 border-l-blue-500 bg-slate-50 p-3">
                            <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-0.5">
                              Guaranteed annuity income {seAnnuityQuote ? '(your quote)' : '(indicative)'}
                            </p>
                            <p className="text-base font-bold text-blue-700">{formatCurrency(leader.annuityIncome)}<span className="text-xs font-medium text-slate-500"> / year for life</span></p>
                            <p className="text-[11px] text-slate-500 mt-0.5">
                              Only 60% of the purchase price counts for the assets test (for the first five years, or until age 85 if that is later, and 30% after that), which can lift the Age Pension.{' '}
                              {seAnnuityQuote
                                ? 'Every strategy above has been re-modelled and re-ranked on this figure.'
                                : 'Indicative — if you have a provider quote, enter it below and every strategy is re-modelled on it.'}
                            </p>
                            <EngineAnnuityTerms
                              className="mt-2"
                              terms={(leader as { annuityTerms?: Record<string, unknown> }).annuityTerms}
                              factFind={factFind}
                            />
                            <AnnuityQuoteControl
                              draft={seAnnuityQuoteDraft}
                              onDraft={(next) => setSeAnnuityQuoteDraft(next === '' ? '' : Number(next))}
                              committed={seAnnuityQuote}
                              onCommit={(next) => { setSeAnnuityQuote(next); if (next == null) setSeAnnuityQuoteDraft(''); }}
                            />
                          </div>
                        ) : undefined}
                      />
                    ))}
                  </div>
                  {/* A plan the horizon rule set aside is said, not hidden — the only note that
                      stays under the cards (owner, 3 Oct 2026: the goals box was more text than
                      understanding; the winners ARE the cards). */}
                  {rvSetAside.length > 0 && (
                    <div className="mt-4 rounded-lg border border-slate-200 border-l-4 border-l-amber-400 bg-white py-2 pl-3 pr-2 text-[13px] leading-relaxed text-slate-700">
                      <p><span className="font-semibold">{rvSetAside.length === 1 ? 'One strategy was set aside.' : `${rvSetAside.length} strategies were set aside.`}</span> A plan is not promoted as the best result when the client&apos;s age and health point to a shorter time frame than it needs.</p>
                      <ul className="mt-1 space-y-1">
                        {rvSetAside.map((sa) => (
                          <li key={sa.strategy.id || sa.strategy.name}>
                            <span className="font-semibold">{sa.strategy.name}</span> would have led on {sa.labels.map((l) => ({ 'Optimal Legacy': 'estate', 'Optimal Cashflow': 'cashflow', 'Optimal Age Pension': 'Age Pension' }[l] || l)).join(' and ')}. {sa.warning}{' '}
                            <button type="button" onClick={() => setSeDeepDive({ projections: sa.strategy.projections || [], name: sa.strategy.name || '' })} className="font-semibold text-blue-700 underline decoration-dotted underline-offset-2 hover:text-blue-900">See it anyway</button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <p className="text-xs text-slate-500 mt-4">These are model illustrations based on what you entered and a standard retirement-village contract. Open the deep dive on any strategy for the full year-by-year detail. To model your own variation, switch to <strong>Manual Control</strong> above.</p>
                </>
              )}
            </Card>
          )}
        </div>

        {/* THE SAME 3/2 SPLIT AS AGED CARE (2026-08-27). A rail narrower than the benchmark's
            renders the same PlanSummaryPanel with less room, and the Financial Impact heading and
            its projections link collapse onto one line and read as a single nonsense phrase.
            `xl:` not `lg:` or `2xl:` is deliberate: lg (1024px) splits far too early for a panel
            this dense, and a 1920 screen at 100% zoom is only a ~1330px viewport, so a 2xl split
            would withhold the panel entirely at the resolution the product is actually used at. */}
        <div className="xl:col-span-2 space-y-6 xl:sticky xl:top-8">
          {/*
            * ONE PANEL, the artifact Aged Care and Support at Home use (owner direction,
            * 2026-08-27: the Aged Care module is the benchmark).
            *
            * The rail was a dark Cost overview card, a baseline toggle and a separate
            * Financial Impact card — the same three things Aged Care had before consolidation, in
            * the same order, with the plan recap far down the page. The move here is smaller than
            * it was there because RV already used the SHARED FinancialImpactPanel rather than a
            * copy of it; what it did not have was one surface tying the costs, the impact and the
            * words together.
            */}
          <PlanSummaryPanel
            strategyName={rvPlanSummaryName}
            baselineLabel="staying put"
            inBrief={rvInBrief}
            onReadFullExplanation={rvPlanSummaryName ? () => setShowRvExplanation(true) : null}
            onViewProjections={() => { setProjectionsSource(railHoldsStayPut ? 'stayPut' : 'plan'); setShowProjectionsModal(true); }}
            /*
             * THE RAIL HOLDS STAYING PUT until Manual Control names a plan (owner, 24 Sep 2026). It
             * used to head "No plan yet" over figures for the move as typed — a plan the app had
             * chosen — the entry contribution from savings — while the engine measured everything
             * against staying put: two baselines in one module. One now. In engine mode the rail
             * says what it is for, and each card carries its own plan.
             */
            noStrategyHeading={strategyMode === 'optimised' ? 'Staying put — for comparison' : 'No plan yet — your current living arrangements'}
            noStrategyNote={strategyMode === 'optimised' ? (
              <><span className="font-semibold text-slate-700">For comparison</span> while you choose. Each strategy card carries its own figures —{' '}
              <span className="font-semibold text-slate-700">Select</span> one, then{' '}
              <span className="font-semibold text-slate-700">Explain this</span> and{' '}
              <span className="font-semibold text-slate-700">Deep Dive</span> on the card.</>
            ) : (
              <>Life as it is today, with no village move. This is the baseline — every way of funding the move is measured against{' '}
              <span className="font-semibold text-slate-700">staying put</span>. The contract below is what the move would cost.</>
            )}
            liquidity={rvRunway && (
              <>
                <p className="text-lg font-bold leading-tight tracking-tight text-slate-900">{rvRunway.headline}</p>
                <div className="mt-2">
                  <FundingRunway
                    coveredPct={rvRunway.coveredPct}
                    leftLabel={rvRunway.leftLabel}
                    rightLabel={rvRunway.rightLabel}
                    depleted={rvRunway.depleted}
                    flag={rvRunway.flag}
                  />
                </div>
                <p className="mt-1.5 text-[11px] text-slate-400">{rvRunway.basis}</p>
              </>
            )}
            costOverviewLabel="What the village costs"
            costOverview={
            <RvVillageCostsTile villageInputs={villageInputs} annualOngoing={summary.annualOngoing}>
            {!railHoldsStayPut && fundFrom === 'liquid' && summary.entryFee > 0 && (
              <p className="text-xs text-gray-400 mt-3 pt-3 border-t border-gray-600">The entry fee is funded from your liquid assets and/or investment property. Default order: bank accounts → term deposits → account-based pensions → shares. Allocate specific amounts in &quot;Entry fee from existing assets&quot; below.</p>
            )}
            {!railHoldsStayPut && fundFrom !== 'liquid' && liquidTargetForEntryFee > 0 && summary.entryFee > 0 && (
              <p className="text-xs text-gray-400 mt-3 pt-3 border-t border-gray-600">The sale covers {formatCurrency(propertyTowardEntry)}; the remaining {formatCurrency(liquidTargetForEntryFee)} comes from savings.</p>
            )}
            {!railHoldsStayPut && fundFrom === 'investment_property' && summary.entryFee > 0 && liquidTargetForEntryFee <= 0 && (
              <p className="text-xs text-gray-400 mt-3 pt-3 border-t border-gray-600">The entry fee is funded from the sale of your investment property. Allocate the amount in the &quot;Sell investment property&quot; section above.</p>
            )}
            {/* A $500,000 hole and a $900 rounding gap are not the same message (owner-caught,
                2026-08-26). The old copy called every shortfall "a small gap" and blamed capital
                gains tax on a share sale — to a client who may hold no shares at all. Which sentence
                appears is now decided by the SIZE of the gap and by what this client actually owns,
                and the structural case says the thing that matters: this village cannot be funded
                from savings, so a property has to be sold. */}
            {!railHoldsStayPut && entryFeeShortfall > 0 && (
              (() => {
                const sharesHeld = (factFind?.assets?.shares || []).reduce((sum, a) => sum + n(a.value), 0);
                // "Small" = plausibly the after-tax haircut on a share sale, which is the only way a
                // fully-allocated plan lands slightly short. Anything larger is a funding gap.
                const isRoundingGap = sharesHeld > 0 && entryFeeShortfall <= Math.max(20_000, sharesHeld * 0.3);
                const fallbacks = [hasHome ? 'selling or downsizing the family home' : null, hasInvestmentProperty ? 'selling an investment property' : null].filter(Boolean) as string[];
                return (
                  /*
                   * SAY WHAT TO DO, not what the heading just said (visual pass, 2026-08-28).
                   *
                   * The panel heading four inches above already reads "Not funded yet — the move as
                   * entered" and names the shortfall. This block repeated the amount and the rule at
                   * length, in amber on a dark card, which is the hardest text on the screen to read
                   * and was the second telling of the same fact.
                   *
                   * It now carries the ledger (allocated of required — which the heading does NOT
                   * give) and the ACTION, and stops. The rule about paying in full is stated once, in
                   * the heading's note.
                   */
                  <div className="text-xs text-amber-200 bg-amber-900/30 mt-3 pt-3 border-t border-amber-600/50">
                    <p className="flex items-baseline justify-between gap-3">
                      <span>Allocated so far</span>
                      <span className="font-semibold tabular-nums">
                        {formatCurrency(totalTowardEntryFee)} of {formatCurrency(entryFeeAmount)}
                      </span>
                    </p>
                    <p className="mt-1.5">
                      {isRoundingGap ? (
                        <>A gap this size is usually <strong>capital gains tax</strong> — a share sale has to cover the tax too, so only the after-tax amount reaches the entry fee. Allocate a little more.</>
                      ) : fallbacks.length > 0 ? (
                        <>Savings cannot cover it. This move only works by {fallbacks.join(' or ')} — use <strong>Property strategy options</strong> above.</>
                      ) : (
                        <>Savings cannot cover it, and there is no property to fund the balance. This village is out of reach on these numbers.</>
                      )}
                    </p>
                  </div>
                );
              })()
            )}
            </RvVillageCostsTile>
            }
            financialImpact={
            <FinancialImpactPanel
              embedded
              /* Staying put IS the panel until a plan is named: one figure per row, no change. */
              baselineOnly={railHoldsStayPut}
              before={initialFinancials ?? { netWorth: 0, cashFlow: 0 }}
              after={(railHoldsStayPut ? initialFinancials : (simulatedFinancials || initialFinancials)) ?? { netWorth: 0, cashFlow: 0 }}
              legacyYears={localLegacyYears}
              setLegacyYears={setLocalLegacyYears}
              onViewProjections={() => { setProjectionsSource(railHoldsStayPut ? 'stayPut' : 'plan'); setShowProjectionsModal(true); }}
              projections={railProjections}
              cashFlowAfterValue={railHoldsStayPut ? initialFinancials?.cashFlow : (noVillageScenario ? initialFinancials?.cashFlow : projections[0]?.netCashflow)}
              cashFlowBreakdownOverride={railHoldsStayPut ? (initialFinancials?.cashFlowBreakdown ?? null) : rvImpactBreakdowns?.cashFlow}
              pensionBreakdownOverride={railHoldsStayPut ? (initialFinancials?.socialSecurityBreakdown ?? null) : rvImpactBreakdowns?.agePension}
              netWorthAfterValue={railHoldsStayPut ? initialFinancials?.netWorth : netWorthAfterDisplay}
              netWorthBreakdownOverride={railHoldsStayPut ? null : rvImpactBreakdowns?.netWorth}
              // The estate tile is measured against STAYING PUT over the same period, so it agrees
              // with the plan summary directly below it rather than contradicting it.
              baselineLabel="staying put"
              legacyBaselineValue={manualBaselineLegacy}
              legacyEstimateOverride={railHoldsStayPut ? manualBaselineLegacy : legacyEstimateDisplay}
              legacyBreakdownOverride={railHoldsStayPut ? null : rvImpactBreakdowns?.legacy}
            />
            }
          />
        </div>
      </div>
      )}

      {/* Every evaluated strategy side by side — the aged care module's transparency: nothing the
          engine compared is hidden behind the goal-leader cards. Selecting here works exactly like
          selecting a leader card. */}
      {showAllSeStrategies && rvSuggestions && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowAllSeStrategies(false)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col border border-slate-200/80" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 pt-6 pb-4 border-b border-slate-100 bg-slate-50 flex items-start justify-between gap-4">
              <div>
                <h2 className="text-2xl font-bold tracking-tight"><GradientText>All modelled strategies</GradientText></h2>
                <p className="text-sm text-gray-600 mt-1">Every strategy the engine evaluated for your position. The goal-leader cards show the strongest per goal; this is the full field they won against. Sort by estate, cashflow or Age Pension.</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <label className="text-sm text-slate-600">Sort by</label>
                <select value={allSortBy} onChange={(e) => setAllSortBy(e.target.value as 'legacy' | 'cashflow' | 'pension')} className="text-sm border border-slate-300 rounded-lg px-2 py-1.5 bg-white">
                  <option value="legacy">Projected estate</option>
                  <option value="cashflow">Net cashflow</option>
                  <option value="pension">Age Pension</option>
                </select>
                <button type="button" onClick={() => setShowAllSeStrategies(false)} className="shrink-0 rounded-lg p-2 text-slate-500 hover:text-slate-800 hover:bg-slate-100" aria-label="Close">✕</button>
              </div>
            </div>
            <div className="p-6 overflow-y-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-slate-200">
                    <th className="py-2 pr-4">Strategy</th>
                    <th className="py-2 pr-4 text-right">Age Pension /yr</th>
                    <th className="py-2 pr-4 text-right">Cashflow (Yr 1)</th>
                    <th className="py-2 pr-4 text-right">Legacy (Yr {localLegacyYears})</th>
                    <th className="py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {([...((rvSuggestions.strategies || []) as RvEvaluatedStrategy[])]
                    .sort((a, b) => {
                      const pick = (s: RvEvaluatedStrategy) => allSortBy === 'cashflow' ? (s.snapshot?.netCashflow ?? 0) : allSortBy === 'pension' ? (s.snapshot?.pension ?? 0) : (s.snapshot?.legacyValue ?? 0);
                      return pick(b) - pick(a);
                    }))
                    .map((s) => {
                      const isSelected = s.id === selectedSeId;
                      return (
                        <tr key={s.id || s.name} className={`border-b border-slate-100 ${isSelected ? 'bg-blue-50/60' : ''}`}>
                          <td className="py-3 pr-4">
                            <p className="font-semibold text-gray-900">{s.name}</p>
                            <p className="text-xs text-gray-500 mt-0.5 max-w-md">{s.description}</p>
                          </td>
                          <td className="py-3 pr-4 text-right font-medium">{formatCurrency(s.snapshot?.pension)}</td>
                          <td className={`py-3 pr-4 text-right font-medium ${(s.snapshot?.netCashflow ?? 0) < 0 ? 'text-rose-700' : 'text-emerald-700'}`}>{formatCurrency(s.snapshot?.netCashflow)}</td>
                          <td className="py-3 pr-4 text-right font-semibold">{formatCurrency(s.snapshot?.legacyValue)}</td>
                          <td className="py-3 text-right whitespace-nowrap">
                            {/* Every row is analysable from here — that is what the list is for.
                                The BASELINE gets no "Select": staying put is not a plan you apply,
                                it is the thing every plan is measured against, and offering Select
                                on it set a selection that then had no card to render (owner-caught,
                                2026-08-26: "it just took me back to the main screen"). */}
                            <div className="inline-flex gap-2">
                              {s.id !== 'rv-stay-home' && (
                                <Button variant={isSelected ? 'secondary' : 'brand'} size="sm" onClick={() => { setSelectedSeId(s.id ?? null); setShowAllSeStrategies(false); }}>
                                  {isSelected ? 'Selected' : 'Select'}
                                </Button>
                              )}
                              <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => { setSeDeepDive({ projections: s.projections || [], name: s.name || '' }); setShowAllSeStrategies(false); }}
                              >
                                Deep dive
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* The shared engine view — the same charts, monthly narrative and per-figure tooltips the Aged
          Care module gets, because these rows come from the same engine and carry the same data. The
          old RVProjectionsModal showed the table alone, which read as a different (poorer) product.
          Village fees are the recurring cost here, so the column says so. */}
      {/* "Understanding your plan" — the recap on its own surface, so its reasons can simply be
          read rather than tapped for one at a time. Same modal and same component as Aged Care. */}
      {showRvExplanation && (
        <PlanExplanationModal
          strategyName={usingSe ? (selectedStrategy?.name ?? null) : rvPlanSummaryName}
          onClose={() => setShowRvExplanation(false)}
          footer={usingSe && selectedStrategy ? (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                const s = selectedStrategy as RvEvaluatedStrategy;
                setShowRvExplanation(false);
                setSeDeepDive({ projections: s.projections || [], name: s.name || '' });
              }}
            >
              See the year-by-year figures
            </Button>
          ) : undefined}
        >
          {rvRecapNode}
          {/* Each piece of the plan in plain words — a client should not have to know what a
              downsizer contribution is to read the sentence above (owner, 24 Sep 2026). */}
          <PlanPiecesCard
            className="mt-4"
            pieces={rvPlanPieces({
              fundFrom: effFundFrom,
              rentHome: effRentHome,
              entryFee: summary.entryFee,
              allocations: usingSe
                ? (((selectedStrategy?.inputs as Record<string, unknown> | undefined)?.sellHomeDetails as Record<string, unknown> | undefined) ?? null)
                : (effDownsizeHome ? (sellHomeDetails as unknown as Record<string, unknown>) : null),
              clientName: factFind?.client?.firstName || 'you',
              partnerName: factFind?.partner?.firstName || 'your partner',
              nilResidualAnnuity: !!nilResidualAnnuityForRecap,
            })}
          />
        </PlanExplanationModal>
      )}
      {/* The FY blend on row 1 is a FINANCIAL-year device, so calendar mode shows the calendar
          run's own rows — the same choice the Aged Care analyser makes. */}
      {showProjectionsModal && projectionsSource === 'stayPut' && (
        <ProjectionsModal
          onClose={() => { setShowProjectionsModal(false); setProjectionsSource('plan'); }}
          projections={stayPutProjections as never}
          factFind={factFind as never}
          initialFinancials={initialFinancials as never}
          costLabel="Village Fees"
          costSource="village"
        />
      )}
      {showProjectionsModal && projectionsSource === 'plan' && (
        <ProjectionsModal
          onClose={() => setShowProjectionsModal(false)}
          projections={(modalPeriodMode === 'calendar_year' && calendarYearProjections ? calendarYearProjections : projectionsForModal) as never}
          onPeriodModeChange={setModalPeriodMode}
          isReloading={modalPeriodMode === 'calendar_year' && !calendarYearProjections}
          factFind={factFind as never}
          initialFinancials={initialFinancials as never}
          costLabel="Village Fees"
          costSource="village"
        />
      )}

      {riskStrategy && (
        <StrategyRisksPanel
          strategyName={riskStrategy.name || ''}
          onClose={() => setRiskStrategy(null)}
          risks={buildStrategyRisks(riskKindsFromCandidateId(String(riskStrategy.id || '')), {
            horizonWarning: annuityHorizonWarningOf(riskStrategy, factFind),
            stampDuty: Number((riskStrategy.inputs as { investmentPropertyStampDuty?: number } | undefined)?.investmentPropertyStampDuty) || null,
          })}
        />
      )}

      {seDeepDive && (
        <ProjectionsModal
          onClose={() => setSeDeepDive(null)}
          projections={seDeepDive.projections as never}
          factFind={factFind as never}
          initialFinancials={initialFinancials as never}
          costLabel="Village Fees"
          costSource="village"
        />
      )}

      {showCompareModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col border border-slate-200/80">
            <div className="px-6 pt-6 pb-4 border-b border-slate-100 bg-slate-50">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold tracking-tight">
                    <GradientText>Strategy Comparison</GradientText>
                  </h2>
                  <p className="text-sm text-slate-600 mt-1.5">Side-by-side view of saved retirement village strategies.</p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowCompareModal(false)}
                  className="shrink-0 rounded-lg p-2 text-slate-500 hover:text-slate-800 hover:bg-slate-100 transition-colors text-2xl leading-none"
                  aria-label="Close"
                >
                  ×
                </button>
              </div>
            </div>
            <div className="overflow-auto flex-1 px-6 py-5">
              <div className="rounded-xl border border-slate-200 overflow-hidden shadow-sm">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="bg-slate-100 text-slate-800">
                      <th className="px-4 py-3 text-left font-semibold border-b border-slate-200/90">Strategy name</th>
                      <th className="px-4 py-3 text-right font-semibold border-b border-slate-200/90 whitespace-nowrap">Entry fee</th>
                      <th className="px-4 py-3 text-right font-semibold border-b border-slate-200/90 whitespace-nowrap">Annual ongoing</th>
                      <th className="px-4 py-3 text-right font-semibold border-b border-slate-200/90 whitespace-nowrap">Age pension (Yr1)</th>
                      <th className="px-4 py-3 text-right font-semibold border-b border-slate-200/90 whitespace-nowrap">Cash flow (Yr1)</th>
                      <th className="px-4 py-3 text-right font-semibold border-b border-slate-200/90 whitespace-nowrap">Legacy estimate</th>
                      <th className="px-4 py-3 text-center font-semibold border-b border-slate-200/90 whitespace-nowrap">Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rvSavedStrategies.map((strategy, index) => {
                      const outputs = strategy.outputs as {
                        entryFee?: number;
                        annualOngoing?: number;
                        agePension?: number;
                        cashFlow?: number;
                        legacyEstimate?: number;
                        legacyAtYear15?: number;
                        summary?: { entryFee?: number; annualOngoing?: number };
                      } | undefined;
                      return (
                      <tr
                        key={strategy.id}
                        className={`border-b border-slate-100 last:border-b-0 transition-colors hover:bg-sky-50/50 ${index % 2 === 0 ? 'bg-white' : 'bg-slate-50/60'}`}
                      >
                        <td className="px-4 py-3 font-medium text-slate-900 align-top">{strategy.name}</td>
                        <td className="px-4 py-3 text-right tabular-nums">{formatCurrency(outputs?.entryFee ?? outputs?.summary?.entryFee)}</td>
                        <td className="px-4 py-3 text-right tabular-nums">{formatCurrency(outputs?.annualOngoing ?? outputs?.summary?.annualOngoing)}</td>
                        <td className="px-4 py-3 text-right tabular-nums">{formatCurrency(outputs?.agePension)}</td>
                        <td className={`px-4 py-3 text-right tabular-nums font-medium ${(outputs?.cashFlow ?? 0) < 0 ? 'text-rose-700' : 'text-emerald-700'}`}>
                          {formatCurrency(outputs?.cashFlow)}
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums">{formatCurrency(outputs?.legacyEstimate ?? outputs?.legacyAtYear15)}</td>
                        <td className="px-4 py-3 text-center text-slate-600 align-top">
                          {new Date(strategy.createdAt || strategy.savedAt || Date.now()).toLocaleDateString()}
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/50 flex justify-end">
              <button
                type="button"
                onClick={() => setShowCompareModal(false)}
                className="px-5 py-2.5 rounded-lg font-semibold text-white bg-gradient-to-r from-blue-600 to-teal-600 hover:from-blue-700 hover:to-teal-700 shadow-sm transition-all"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export interface RetirementVillagePageProps {
  setActivePage: (page: string) => void;
  factFind: DraftFactFind;
  savedStrategies: SavedStrategy[];
  setSavedStrategies: React.Dispatch<React.SetStateAction<SavedStrategy[]>>;
  /** Persists the new-money profile answer to the client record (App owns the fact-find state). */
  setFactFind?: React.Dispatch<React.SetStateAction<DraftFactFind>>;
  /**
   * Called with each generated Statement of Advice (the strategy plus its document snapshot) so it
   * lands on the server's advice register like aged care's (owner, 2 Oct 2026). App.tsx decides where.
   */
  onSoaGenerated?: (document: Record<string, unknown>) => void;
}

const RetirementVillagePage = ({ setActivePage, factFind, savedStrategies, setSavedStrategies, setFactFind, onSoaGenerated }: RetirementVillagePageProps) => {
  const [view, setView] = useState<'explanation' | 'calculator'>('explanation');
  // Selected state/territory drives the consumer-protection content; held here so it persists across the
  // explainer ↔ calculator toggle and can later feed the analyser's estate-timing narrative.
  const [rvState, setRvState] = useState('');
  useEffect(() => {
    if (view === 'calculator') {
      scrollAppToTop();
    }
  }, [view]);
  if (view === 'explanation') return <RetirementVillageExplained setActivePage={setActivePage} onContinue={() => setView('calculator')} rvState={rvState} setRvState={setRvState} />;
  return <RetirementVillageAnalyser setActivePage={setActivePage} factFind={factFind} onBack={() => setView('explanation')} savedStrategies={savedStrategies} setSavedStrategies={setSavedStrategies} rvState={rvState} setFactFind={setFactFind} onSoaGenerated={onSoaGenerated} />;
};

export default RetirementVillagePage;
